// frontend/src/PipelineRunnerPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import DocumentViewerModal from './DocumentViewerModal';
import { extractSpecialistProposalFileText, extractSpecialistProposalFileData, finalizeSpecialistProposal, fixFileStructure, loadSpecialistProposalChatHistory, reformatSpecialistProposalAvailabilityFile } from './api/hutcheonMearns';
import { normalizeToHomeRelative } from './utils/fileExtraction';
import { useChatDisplay } from './chat/useChatDisplay';
import { ChatMessagesList } from './chat/ChatMessagesList.jsx';
import { buildMarkdownOptions } from './chat/markdownOptions';
import { annotateAgentSteps } from './chat/useAgentSteps';
import { streamSSE } from './chat/streamSSE';
import { makeSSEHandlers } from './chat/makeSSEHandlers';
import './PipelineRunnerPage.css';
import './chat/chat.css';
import { ChatShell } from './chat/ChatShell.jsx';

const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

function PipelineRunnerPage({ authUsername, userLLMSettings, defaultPipelineName = 'Specialist_Proposal_Pipeline_v3', handleLogout, onBackendBusyChange }) {
  const [input, setInput] = useState({ companyName: '', companyDomainUrl: '', meetingNotes: '', expectedStartDate: '' });
  const [touched, setTouched] = useState({ companyName: false, companyDomainUrl: false, meetingNotes: false, expectedStartDate: false });
  const [loading, setLoading] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [showUpdateProfileForm, setShowUpdateProfileForm] = useState(false);
  const [updateProfileName, setUpdateProfileName] = useState('');
  const [updateProfileFile, setUpdateProfileFile] = useState(null);
  const [updateProfileStatus, setUpdateProfileStatus] = useState('');
  const [updateProfileJobId, setUpdateProfileJobId] = useState(null);
  const [updateProfileJobStart, setUpdateProfileJobStart] = useState(null);
  const [specialistProfiles, setSpecialistProfiles] = useState([]);
  const [specialistOptionsLoading, setSpecialistOptionsLoading] = useState(false);
  const [specialistOptionsError, setSpecialistOptionsError] = useState('');
  const [updatingProfile, setUpdatingProfile] = useState(false);
  // Selected chat to load (unwired dropdown for now)
  const [selectedChatIdToLoad, setSelectedChatIdToLoad] = useState('');
  // Chats saved with SPA_HM_Custom prompt
  const [availableChats, setAvailableChats] = useState([]);
  const [chatListError, setChatListError] = useState('');

  const [selectedPipeline, setSelectedPipeline] = useState(defaultPipelineName);
  const [loopsBeforeHumanInput] = useState(9);

  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null); // for pause/resume support
  const chatIdRef = useRef(chatId);
  useEffect(() => { chatIdRef.current = chatId; }, [chatId]);
  // After first submission, switch to normal single-line chat input
  const [hasInitialSubmitted, setHasInitialSubmitted] = useState(false);
  const [secondaryInput, setSecondaryInput] = useState('');
  const [extraUserInputs, setExtraUserInputs] = useState([]); // user messages to apply on next resume
  const [pipelinePrompts, setPipelinePrompts] = useState([]);
  const [pauseAfterStep, setPauseAfterStep] = useState(pipelinePrompts.map((_, idx) => idx + 1)); // select all steps by default
  const pauseAfterStepRef = useRef(pauseAfterStep);
  useEffect(() => { pauseAfterStepRef.current = pauseAfterStep; }, [pauseAfterStep]);
  // Cancellation support
  const cancelRequestedRef = useRef(false);


  const [, setFinalOutput] = useState(null); // text or object
  const [generatedFiles, setGeneratedFiles] = useState([]); // array of Home-relative paths from backend
  const [extractedEntries, setExtractedEntries] = useState([]); // [{ filePath, fileNumber, text, loading, error, specialists, profiles, selectedIndex, profileExperienceOpen, criteriaOpen, ... }]
  // Keep refs for latest values so event handlers can read synchronously (avoid stale closures)
  const generatedFilesRef = useRef(generatedFiles);
  useEffect(() => { generatedFilesRef.current = generatedFiles; }, [generatedFiles]);
  const extractedEntriesRef = useRef(extractedEntries);
  useEffect(() => { extractedEntriesRef.current = extractedEntries; }, [extractedEntries]);

  const applyBackendFiles = useCallback((paths) => {
    const normalized = Array.isArray(paths)
      ? paths.map((p) => normalizeToHomeRelative(p)).filter(Boolean)
      : [];
    setGeneratedFiles(normalized);
    return normalized;
  }, []);

  const openSettingsModal = useCallback(() => setSettingsModalOpen(true), []);
  const closeSettingsModal = useCallback(() => setSettingsModalOpen(false), []);

  // Pause/resume state
  // pausedStepIndex is 0-based index from backend (internal/external: current step; checkpoint: next step to run)
  const [pausedStepIndex, setPausedStepIndex] = useState(-1);
  const [, setPausedPipelineName] = useState('');
  const pauseTypeRef = useRef(''); // 'internal' | 'external' | 'checkpoint' (backend-provided)
  const isInternalPauseRef = useRef(false); // derived from pauseTypeRef for legacy fallback
  const [loadCheckpointStep, setLoadCheckpointStep] = useState('');
  const [chatExpanded, setChatExpanded] = useState(false);
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  });
  // Keep a ref in sync with pausedStepIndex to avoid stale reads between async updates
  const pausedStepIndexRef = useRef(pausedStepIndex);
  useEffect(() => { pausedStepIndexRef.current = pausedStepIndex; }, [pausedStepIndex]);

  useEffect(() => {
    const handleResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track last started/completed steps (1-based) to support "Rerun step"
  const lastStepStartedRef = useRef(0);
  const lastStepCompletedRef = useRef(0);

  // Message buffer shared across runs so handlers can push messages synchronously
  const pipelineMessagesRef = useRef([]);
  const pushPipelineMessage = (msg) => {
    pipelineMessagesRef.current.push(msg);
    setMessages([...pipelineMessagesRef.current]);
  };

  // Helpers to report parsing format errors back into the chat stream
  const isServerError500 = (err) => {
    try {
      if (!err) return false;
      if (err.response && typeof err.response.status === 'number') return err.response.status === 500;
      if (typeof err.status === 'number') return err.status === 500;
      const m = String(err.message || err).toLowerCase();
      return m.includes('500') || m.includes('internal server error');
    } catch { return false; }
  };
  const notifyWrongFormat = (filePath, fileNumber) => {
    const rel = String(filePath || '').replace(/^Home\//, '');
    const display = rel.split('/').pop() || rel || 'the file';
    const stepLabel = (typeof fileNumber === 'number' && fileNumber >= 1) ? ` for step ${fileNumber}` : '';
    pushPipelineMessage({
      role: 'assistant',
      content: `Observation: Parsing failed (HTTP 500)${stepLabel} – ${display}. The output appears to be in the wrong format. Please regenerate this step using the expected schema/structure.`,
    });
  };

  // Read full file content via existing read_user_files endpoint
  const readUserFileContent = useCallback(async (path) => {
    const readUrl = `${BASE_URL}/api/file_explorer/read_user_files`;
    const resp = await fetch(readUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ filePaths: [path] }),
    });
    if (!resp.ok) throw new Error(`Read failed: ${resp.status}`);
    const j = await resp.json();
    return j?.combinedContent ?? '';
  }, [BASE_URL]);

  // Overwrite file content via update_txt_file
  const overwriteUserFileContent = useCallback(async (path, newContent) => {
    const updateUrl = `${BASE_URL}/api/file_explorer/update_txt_file`;
    const resp = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ filePath: path, newContent }),
    });
    if (!resp.ok) throw new Error(`Update failed: ${resp.status}`);
    return true;
  }, [BASE_URL]);

  // Avoid repeated auto-fix attempts per file during one session
  const autoFixedRef = useRef(new Set());

  // -------------------------
  // File 4: JSON specialist profiles helpers
  // -------------------------
  const stripCodeFences = (txt) => {
    if (typeof txt !== 'string') return '';
    const s = txt.trim();
    // Remove ```json ... ``` or ``` ... ``` wrappers if present
    if (s.startsWith('```')) {
      const firstNewline = s.indexOf('\n');
      const header = firstNewline === -1 ? s : s.slice(0, firstNewline);
      const rest = firstNewline === -1 ? '' : s.slice(firstNewline + 1);
      const tail = rest.lastIndexOf('```');
      if (tail !== -1) {
        // ignore language label in header
        return rest.slice(0, tail).trim();
      }
      // If no closing fence, just remove leading fence line.
      if (header.startsWith('```')) return rest.trim();
    }
    return s;
  };

  const extractLikelyJsonSubstring = (txt) => {
    if (typeof txt !== 'string') return null;
    const s = txt;
    const iObj = s.indexOf('{');
    const iArr = s.indexOf('[');
    let start = -1;
    if (iObj === -1 && iArr === -1) return null;
    if (iObj === -1) start = iArr;
    else if (iArr === -1) start = iObj;
    else start = Math.min(iObj, iArr);

    const isArray = s[start] === '[';
    const end = isArray ? s.lastIndexOf(']') : s.lastIndexOf('}');
    if (end === -1 || end <= start) return null;
    return s.slice(start, end + 1);
  };

  const tryParseJsonFromText = (txt) => {
    if (typeof txt !== 'string') return null;
    const cleaned = stripCodeFences(txt);
    // First try: direct parse
    try {
      return JSON.parse(cleaned);
    } catch { /* continue */ }
    // Second try: parse a likely JSON substring
    const sub = extractLikelyJsonSubstring(cleaned);
    if (sub) {
      try {
        return JSON.parse(sub);
      } catch { /* continue */ }
    }
    return null;
  };

  const pickProfilesArrayFromRoot = (root) => {
    // Returns { profiles, containerKey, rootShape }
    // - rootShape: 'array' | 'object'
    // - containerKey: when object, which key contains the profiles array
    if (!root) return { profiles: null, containerKey: null, rootShape: null };
    if (Array.isArray(root)) return { profiles: root, containerKey: null, rootShape: 'array' };
    if (typeof root !== 'object') return { profiles: null, containerKey: null, rootShape: null };

    const candidateKeys = [
      'proposed_specialist_profiles',
      'specialist_profiles',
      'proposed_profiles',
      'profiles',
      'specialists',
      'proposed_specialists',
      'items',
      'data',
    ];
    for (const k of candidateKeys) {
      const v = root[k];
      if (Array.isArray(v)) return { profiles: v, containerKey: k, rootShape: 'object' };
      // Sometimes nested: data: { profiles: [...] }
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const nested = v.profiles || v.specialist_profiles || v.proposed_specialist_profiles || v.items;
        if (Array.isArray(nested)) return { profiles: nested, containerKey: `${k}.__nested__`, rootShape: 'object' };
      }
    }
    return { profiles: null, containerKey: null, rootShape: 'object' };
  };

  const normalizeSkills = (v) => {
    if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
    if (typeof v === 'string') {
      return v.split(',').map(x => x.trim()).filter(Boolean);
    }
    return [];
  };

  const safeNameFromProfile = (p, idx) => {
    const n = p?.name || p?.full_name || p?.candidate_name || p?.consultant_name;
    const s = typeof n === 'string' ? n.trim() : '';
    return s || `Specialist ${idx + 1}`;
  };

  const friendlyNameFromFileName = (fileName) => {
    if (!fileName) return '';
    const withoutExt = String(fileName).replace(/\.[^.]+$/, '');
    let cleaned = withoutExt.replace(/[_-]+/g, ' ');
    cleaned = cleaned.replace(/\s*profile\b.*$/i, '');
    cleaned = cleaned.replace(/\s*,\s*/g, ' ');
    cleaned = cleaned.trim();
    if (!cleaned) return '';
    return cleaned
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildProfileFilename = (displayName) => {
    const trimmed = String(displayName || '').trim();
    if (!trimmed) return 'profile.pptx';
    const parts = trimmed.split(/\s+/);
    const first = parts.shift() || '';
    const last = parts.join(' ');
    const nameWithComma = last ? `${first}, ${last}` : first;
    return `${nameWithComma}_profile.pptx`;
  };

  const buildSearchableProfileSlug = (displayName, rawFileName) => {
    if (rawFileName) {
      return String(rawFileName).replace(/\.[^.]+$/, '');
    }
    const trimmed = String(displayName || '').trim();
    if (!trimmed) return '';
    return `${trimmed.replace(/[,\s]+/g, '_')}_profile`;
  };

  const specialistNameOptionsFromEntries = useMemo(() => {
    const names = new Set();
    (Array.isArray(extractedEntries) ? extractedEntries : []).forEach((entry) => {
      if (Array.isArray(entry?.profiles)) {
        entry.profiles.forEach((p, idx) => names.add(safeNameFromProfile(p, idx)));
      }
      if (Array.isArray(entry?.specialists)) {
        entry.specialists.forEach((p, idx) => names.add(safeNameFromProfile(p, idx)));
      }
    });
    return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [extractedEntries]);

  const specialistNameOptions = useMemo(() => {
    if (Array.isArray(specialistProfiles) && specialistProfiles.length > 0) {
      return specialistProfiles.map((p) => p.displayName);
    }
    return specialistNameOptionsFromEntries;
  }, [specialistProfiles, specialistNameOptionsFromEntries]);

  const requestPauseToServer = async (cid) => {
    if (!cid) return;
    try {
      await fetch(`${BASE_URL}/api/pipelines/request-pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ chat_id: cid }),
      });
      console.log('Requested pause for chat', cid);
    } catch (e) {
      console.warn('Failed to request pause', e);
    }
  };

  const handleNeverChange = () => {
    setPauseAfterStep([]);
  };

  const handlePauseAfterChange = (stepNum) => {
    setPauseAfterStep((prev) => {
      if (prev.includes(stepNum)) {
        return prev.filter((s) => s !== stepNum);
      } else {
        return [...prev, stepNum];
      }
    });
  };

  const toggleUpdateProfileForm = () => {
    setShowUpdateProfileForm((prev) => {
      const next = !prev;
      if (!next) {
        setUpdateProfileName('');
        setUpdateProfileFile(null);
        setUpdateProfileStatus('');
      }
      return next;
    });
  };

  const handleUpdateProfileFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;
    setUpdateProfileFile(file);
    setUpdateProfileStatus('');
  };

  const fetchSpecialistProfiles = useCallback(async () => {
    setSpecialistOptionsLoading(true);
    setSpecialistOptionsError('');
    try {
      const resp = await axios.get(`${BASE_URL}/api/search_files`, {
        params: {
          searchTerm: '',
          filters: JSON.stringify({}),
          selectedDirectory: 'HM-Specialists-dir',
        },
        withCredentials: true,
        validateStatus: () => true,
      });
      const files = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const seen = new Set();
      const normalized = [];
      files
        .filter((f) => typeof f?.file_name === 'string' && /\.pptx$/i.test(f.file_name))
        .forEach((f) => {
          const displayName = friendlyNameFromFileName(f.file_name);
          if (!displayName || seen.has(displayName)) return;
          seen.add(displayName);
          normalized.push({ displayName, fileName: f.file_name, fileId: f.id });
        });
      setSpecialistProfiles(normalized);
      if (normalized.length === 0) {
        setSpecialistOptionsError('No .pptx specialist profiles found in HM-Specialists-dir.');
      }
    } catch (err) {
      console.error('PipelineRunnerPage: failed to load specialist profiles', err);
      setSpecialistProfiles([]);
      setSpecialistOptionsError('Failed to load specialist profiles.');
    } finally {
      setSpecialistOptionsLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    if (!showUpdateProfileForm) return;
    fetchSpecialistProfiles();
  }, [showUpdateProfileForm, fetchSpecialistProfiles]);

  useEffect(() => {
    if (!updateProfileJobId) return undefined;
    let cancelled = false;
    let intervalId;

    const poll = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/job-processing-progress/${updateProfileJobId}`, {
          withCredentials: true,
          validateStatus: () => true,
        });
        const data = res?.data || {};
        if (data?.startedAt && !updateProfileJobStart) {
          setUpdateProfileJobStart(new Date(data.startedAt));
        }
        const status = String(data?.status || '').toUpperCase();

        if (status === 'COMPLETED' || status === 'PARTIAL') {
          setUpdateProfileStatus(status === 'COMPLETED'
            ? 'Profile uploaded and processed successfully.'
            : 'Profile upload finished with partial status; please verify the file.');
          setUpdateProfileJobId(null);
          setUpdateProfileJobStart(null);
          setUpdateProfileFile(null);
          setUpdatingProfile(false);
          fetchSpecialistProfiles();
          return;
        }

        if (status === 'FAILED') {
          setUpdateProfileStatus(`Upload job failed: ${data?.error || 'Unknown error'}`);
          setUpdateProfileJobId(null);
          setUpdateProfileJobStart(null);
          setUpdatingProfile(false);
          return;
        }

        const pct = typeof data?.progress_percent === 'number' ? data.progress_percent : data?.progress;
        const pctText = typeof pct === 'number' ? ` (${Math.round(pct)}%)` : '';
        setUpdateProfileStatus(`Processing uploaded profile...${pctText}`);
      } catch (err) {
        if (!cancelled) {
          setUpdateProfileStatus('Processing status check failed; retrying...');
        }
      }
    };

    poll();
    intervalId = setInterval(poll, 5000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [updateProfileJobId, updateProfileJobStart, BASE_URL, fetchSpecialistProfiles]);

  const handleConfirmUpdateProfile = async () => {
    if (!updateProfileName) {
      setUpdateProfileStatus('Please select a specialist name first.');
      return;
    }
    if (!updateProfileFile) {
      setUpdateProfileStatus('Please choose a file to upload.');
      return;
    }

    setUpdatingProfile(true);
    setUpdateProfileJobId(null);
    setUpdateProfileJobStart(null);
    setUpdateProfileStatus('Looking up existing profile...');

    const selectedProfile = specialistProfiles.find((p) => p.displayName === updateProfileName);
    const searchSlug = buildSearchableProfileSlug(updateProfileName, selectedProfile?.fileName);
    let existingId = null;

    try {
      const filters = { directory: 'HM-Specialists-dir', file_name: searchSlug };
      const resp = await axios.get(`${BASE_URL}/api/search_files`, {
        params: {
          searchTerm: '',
          filters: JSON.stringify(filters),
          selectedDirectory: 'HM-Specialists-dir',
        },
        withCredentials: true,
        validateStatus: () => true,
      });
      const results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const lowered = (searchSlug || '').toLowerCase();
      const match = results.find((r) => typeof r?.file_name === 'string' && r.file_name.toLowerCase().includes(lowered)) || results[0];
      existingId = match?.id ?? null;
      if (existingId) {
        setUpdateProfileStatus('Found existing profile; deleting...');
      } else {
        setUpdateProfileStatus('No existing profile found; uploading new file...');
      }
    } catch (err) {
      console.error('PipelineRunnerPage: search_files failed', err);
      setUpdateProfileStatus('Search failed; continuing to upload new file.');
    }

    if (existingId) {
      try {
        await axios.delete(`${BASE_URL}/api/files/delete_file_by_id/${existingId}`, { withCredentials: true });
        setUpdateProfileStatus('Deleted old profile; uploading new file...');
      } catch (err) {
        console.error('PipelineRunnerPage: delete_file_by_id failed', err);
        setUpdateProfileStatus('Failed to delete existing profile; aborting upload.');
        setUpdatingProfile(false);
        return;
      }
    }

    try {
      const targetFileName = buildProfileFilename(updateProfileName);
      const renamedFile = new File([updateProfileFile], targetFileName, {
        type: updateProfileFile.type || 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });

      const formData = new FormData();
      formData.append('directory', 'HM-Specialists-dir');
      formData.append('processingFlag', 'true');
      formData.append('fullVision', 'true');
      formData.append('customPromptSummaryID', '');
      formData.append('customPromptExtractionID', '');
      formData.append('uploaded_files', renamedFile);

      setUpdateProfileStatus('Uploading new profile...');
      const uploadResp = await axios.post(`${BASE_URL}/api/upload-and-save`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        validateStatus: () => true,
      });

      if (uploadResp.status >= 200 && uploadResp.status < 300) {
        const newJobId = uploadResp?.data?.jobId;
        if (newJobId) {
          setUpdateProfileJobId(newJobId);
          setUpdateProfileStatus('Upload queued; processing new profile...');
          return; // keep updatingProfile true until job completes
        }
        setUpdateProfileStatus('Profile uploaded (no job id returned).');
        setUpdateProfileFile(null);
        setUpdatingProfile(false);
        fetchSpecialistProfiles();
      } else {
        const errMsg = uploadResp?.data?.error || uploadResp?.data?.message || 'Upload failed.';
        setUpdateProfileStatus(errMsg);
        setUpdatingProfile(false);
      }
    } catch (err) {
      console.error('PipelineRunnerPage: upload-and-save failed', err);
      setUpdateProfileStatus('Upload failed.');
      setUpdatingProfile(false);
    }
  };

  // Handler to load previously saved chat via selected chat id
  const handleLoadPreviousChat = useCallback(() => {
    const id = parseInt(selectedChatIdToLoad, 10);
    if (!Number.isNaN(id)) {
      loadPipelineHistory(id);
    } else {
      setError('Select a chat to load.');
    }
  }, [selectedChatIdToLoad]);

  // Fetch all chats and filter to SPA_HM_Custom prompt
  const fetchPipelineChats = useCallback(async () => {
    try {
      setChatListError('');
      const resp = await axios.get(`${BASE_URL}/api/chat-history`, { withCredentials: true });
      const list = Array.isArray(resp.data) ? resp.data : [];
      const filtered = list.filter((c) => {
        const pn = c?.prompt_name ?? c?.promptName;
        return pn === 'SPA_HM_Custom';
      });
      setAvailableChats(filtered);
    } catch (e) {
      console.error('PipelineRunnerPage: Failed to fetch chat history:', e);
      setAvailableChats([]);
      setChatListError('Failed to load saved chats.');
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchPipelineChats();
  }, [fetchPipelineChats]);

  // Generic event handler used by both initial run and resume
  const handleEvent = async (eventType, data) => {
    // If user requested cancellation, stop streaming on next event and try to pause server-side
    if (cancelRequestedRef.current) {
      try {
        const cid = chatIdRef.current;
        if (cid) await requestPauseToServer(cid);
      } catch (e) { /* non-fatal */ }
      pushPipelineMessage({ role: 'system', content: 'Cancellation requested. Attempting to pause…' });
      cancelRequestedRef.current = false;
      setLoading(false);
      return true; // stop local stream
    }
    if (eventType === 'pipeline_started') {
      const serverChatId = data?.chat_id;
      if (serverChatId) {
        // write into the ref immediately so other handlers can read it synchronously
        chatIdRef.current = serverChatId;
        setChatId(serverChatId);
        console.log('Pipeline started, assigned Chat ID:', serverChatId);
        setHasInitialSubmitted(true);
      }
    } else if (eventType === 'step_start') {
      pushPipelineMessage({ role: 'system', content: `--- Step ${data.step}/${data.total_steps} — ${data.name} ---` });
      // Update step progress trackers
      try {
        const current = Number(data.step || 0);
        if (!Number.isNaN(current) && current > 0) {
          lastStepStartedRef.current = current;       // just started this step (1-based)
          // previous step completed is current - 1
          lastStepCompletedRef.current = Math.max(lastStepCompletedRef.current, current - 1);
        }
      } catch (e) { /* noop */ }
      // If user selected a pause-after-step, request the pause while the step is running
      try {
        const stepNum = Number(data.step || -1);
        // set target to true if pauseAfterStep includes stepNum
        const target = pauseAfterStepRef.current.includes(stepNum);
        if (target) {
          const cid = chatIdRef.current;
          if (cid) {
            console.log(`Step ${stepNum} started and matches pause target (${target}). Sending pause request for chat ${cid}`);
            await requestPauseToServer(cid);
          } else {
            console.warn('Step matched pause target but no chat id is available yet.');
          }
        }
      } catch (e) { /* non-fatal */ }
    } else if (eventType === 'new_message') {
      pushPipelineMessage(data);
    } else if (eventType === 'pipeline_paused') {
      const rawPsi = Number(data?.paused_step_index ?? -1);
      setPausedStepIndex(rawPsi);
      pausedStepIndexRef.current = rawPsi;
      console.log('Pipeline paused at step index', rawPsi);
      // Treat backend state as the source of truth.
      try {
        const acc = Array.isArray(data?.accumulated_messages) ? data.accumulated_messages : null;
        if (acc && acc.length) {
          const cleanedMsgs = acc.map(m => ({ role: String(m.role || ''), content: String(m.content || '') }));
          pipelineMessagesRef.current = cleanedMsgs;
          setMessages(cleanedMsgs);
        }
      } catch { /* noop */ }
      setPausedPipelineName(selectedPipeline);

      const pauseType = String(data?.pause_type || '').toLowerCase();
      pauseTypeRef.current = pauseType;
      if (pauseType === 'internal') {
        isInternalPauseRef.current = true;
      } else if (pauseType === 'external' || pauseType === 'checkpoint') {
        isInternalPauseRef.current = false;
      }

      const lastStarted = Number(lastStepStartedRef.current || 0); // 1-based step number of the active agent
      let internalPause = false;
      if (!pauseTypeRef.current) {
      if (!Number.isNaN(rawPsi) && rawPsi >= 0 && lastStarted > 0) {
        // Backend reports zero-based index for the active step when pausing internally.
        // Example: step 4 (1-based) => paused_step_index of 3.
        if (rawPsi + 1 === lastStarted) internalPause = true;
      }
      if (!internalPause) {
        try {
          const msg = String(data?.message || '').toLowerCase();
          if (msg.includes('awaiting user input') || msg.includes('awaiting your input') || msg.includes('provide additional information')) {
            internalPause = true;
          }
        } catch { /* noop */ }
      }
      isInternalPauseRef.current = internalPause;
      }
      // External pause implies the last started step has completed
      if (!isInternalPauseRef.current) {
        lastStepCompletedRef.current = lastStepStartedRef.current || 0;
      }
      pushPipelineMessage({ role: 'system', content: data?.message || 'Pipeline paused by user.' });
      setLoading(false);
      return true;
    } else if (eventType === 'pipeline_complete') {
      if (Array.isArray(data.generated_files) && data.generated_files.length) {
        applyBackendFiles(data.generated_files);
        scheduleReExtractForPaths(data.generated_files);
      }
      // Mark the last started step as completed
      lastStepCompletedRef.current = lastStepStartedRef.current || 0;
      if (typeof data.final_output !== 'undefined') setFinalOutput(data.final_output);
      pushPipelineMessage({ role: 'system', content: data.message || 'Pipeline complete.' });
      setLoading(false);

      // Treat state as external pause
      setPausedStepIndex(-1);
      pausedStepIndexRef.current = -1;
      pauseTypeRef.current = '';
      return true;
    } else if (eventType === 'pipeline_files') {
      const latest = Array.isArray(data.latest_files)
        ? data.latest_files
        : (Array.isArray(data.files) ? data.files : []);
      if (latest.length) {
        applyBackendFiles(latest);
        scheduleReExtractForPaths(latest);
      }
    } else if (eventType === 'error') {
      setError(data.message || 'Pipeline error.');
      setLoading(false);
      console.error('Pipeline error event received:', data);
      return true;
    }
    return false;
  };

  const handleCancelPipeline = useCallback(() => {
    if (!loading) return;
    cancelRequestedRef.current = true;
  }, [loading]);

  const execPipelineAction = useCallback(async ({ action, chatIdOverride }) => {
    const payload = {
      pipeline_name: selectedPipeline,
      chat_id: chatIdOverride || chatIdRef.current,
      selected_tools: {},
      human_loop_setting: loopsBeforeHumanInput,
      action,
    };

    const resp = await fetch(`${BASE_URL}/api/pipelines/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!resp.ok) throw new Error(`Pipeline request failed: ${resp.status}`);
    if (!resp.body) throw new Error('No response body from pipeline execution.');
    await streamSSE(resp, makeSSEHandlers(handleEvent));
  }, [BASE_URL, selectedPipeline, loopsBeforeHumanInput]);

  // Simulator-style controls:
  // - Msg: always sends user_message (even empty) to continue the CURRENT step.
  // - Next Step: advances based on pause_type.
  const sendMsgAction = async (userText = '') => {
    console.log('Msg action at paused step', pausedStepIndexRef.current);
    if (!chatIdRef.current) {
      console.warn('No chat id available to send msg action.');
      return;
    }
    if (pausedStepIndexRef.current < 0) {
      console.warn('No paused step recorded.');
      return;
    }

    const trimmed = typeof userText === 'string' ? userText.trim() : '';
    const queued = (Array.isArray(extraUserInputs) ? extraUserInputs : [])
      .map(m => String(m?.content || '').trim())
      .filter(Boolean)
      .join('\n\n');
    const combined = [queued, trimmed].filter(Boolean).join('\n\n');

    try {
      setLoading(true);
      // Clear paused state (we'll set again if paused later)
      setPausedStepIndex(-1);
      pausedStepIndexRef.current = -1;
      setPausedPipelineName('');
      pauseTypeRef.current = '';
      // Extra inputs have been applied
      setExtraUserInputs([]);

      await execPipelineAction({ action: { type: 'user_message', content: combined || '' } });
      setLoading(false);
    } catch (e) {
      console.error('Msg action failed', e);
      setError(e.message || 'Failed to send message');
      setLoading(false);
    }
  };

  const sendNextStepAction = async () => {
    console.log('Next Step action at paused step', pausedStepIndexRef.current);
    if (!chatIdRef.current) {
      console.warn('No chat id available to advance step.');
      return;
    }
    if (pausedStepIndexRef.current < 0) {
      console.warn('No paused step recorded.');
      return;
    }

    const pauseType = String(pauseTypeRef.current || '').toLowerCase();
    const action = (pauseType === 'internal')
      ? { type: 'skip_step' }
      : (pauseType === 'external')
        ? { type: 'next_step' }
        : { type: 'continue' }; // checkpoint/unknown

    try {
      setLoading(true);
      // Clear paused state (we'll set again if paused later)
      setPausedStepIndex(-1);
      pausedStepIndexRef.current = -1;
      setPausedPipelineName('');
      pauseTypeRef.current = '';
      // Do NOT clear queued inputs here; Next Step should not implicitly apply user text.

      await execPipelineAction({ action });
      setLoading(false);
    } catch (e) {
      console.error('Next Step action failed', e);
      setError(e.message || 'Failed to advance step');
      setLoading(false);
    }
  };

  // Core loader for checkpoints using zero-based step index
  const loadCheckpointCore = async (stepIndexZeroBased) => {
    console.log('Loading checkpoint for step index', stepIndexZeroBased);
    // Special case: -1 means reset to the very beginning (before any checkpoint)
    if (stepIndexZeroBased === -1) {
      try {
        setLoading(true);
        // Reset UI to initial state
        setHasInitialSubmitted(false);
        setInput({ companyName: '', companyDomainUrl: '', meetingNotes: '', expectedStartDate: '' });
        setTouched({ companyName: false, companyDomainUrl: false, meetingNotes: false, expectedStartDate: false });
        pipelineMessagesRef.current = [];
        setMessages([]);
        setFinalOutput(null);
        setGeneratedFiles([]);
        setExtractedEntries([]);
        setError('');
        setPausedStepIndex(-1);
        pausedStepIndexRef.current = -1;
        setPausedPipelineName('');
        pauseTypeRef.current = '';
        isInternalPauseRef.current = false;
        setLoadCheckpointStep('');
        lastStepStartedRef.current = 0;
        lastStepCompletedRef.current = 0;
        setLogoLoadFailed(false);
        setLogoCacheBuster(Date.now());
        // Clear chat id to ensure a fresh run on next start
        chatIdRef.current = null;
        setChatId(null);
      } finally {
        setLoading(false);
      }
      return -1;
    }
    if (!chatIdRef.current) {
      setError('No chat id available to load checkpoint for.');
      return;
    }
    const stepIndex = (stepIndexZeroBased === null || typeof stepIndexZeroBased === 'undefined')
      ? null
      : stepIndexZeroBased;

    if (stepIndex !== null) {
      if (typeof stepIndex !== 'number' || Number.isNaN(stepIndex) || stepIndex < 0) {
        setError('Invalid step index to load.');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');
      // Load checkpoint into backend state and pause immediately.
      const action = (typeof stepIndex === 'number')
        ? { type: 'reload_step', step_index: stepIndex }
        : { type: 'reload_step' };
      await execPipelineAction({ action });
      setLoading(false);
      return typeof stepIndex === 'number' ? stepIndex : 0;
    } catch (e) {
      console.error('Load checkpoint failed', e);
      setError(e?.message || 'Failed to load checkpoint');
      setLoading(false);
    }
  };

  const handleLoadCheckpoint = async () => {
    // Empty input loads latest checkpoint; otherwise treat as 1-based index
    const raw = loadCheckpointStep;
    if (!raw || raw === '') {
      if (!chatIdRef.current) {
        setError('No chat id available to load checkpoint for.');
        return;
      }
      await loadCheckpointCore(null);
      return;
    }
    const stepIndex = parseInt(raw, 10);
    if (Number.isNaN(stepIndex) || stepIndex < 0) {
      setError('Step index must be a non-negative integer.');
      return;
    }
    // If user requested step 0, reset to beginning (-1)
    if (stepIndex === 0) {
      await loadCheckpointCore(-1);
      return;
    }
    if (!chatIdRef.current) {
      setError('No chat id available to load checkpoint for.');
      return;
    }
    await loadCheckpointCore(stepIndex - 1);
  };

  // Rerun the last completed step by loading its checkpoint (checkpoint taken just before that step ran)
  const rerunLastStep = async () => {
    if (!chatIdRef.current) {
      setError('No chat id available to rerun step for.');
      return;
    }
    const completed = Number(lastStepCompletedRef.current || 0);
    if (!completed || completed < 1) {
      setError('No previously completed step to rerun yet.');
      return;
    }
    console.log('Rerunning last completed step', completed);
    const idx0 = await loadCheckpointCore(completed - 2);
    if (typeof idx0 === 'number' && idx0 >= 0) {
      await sendNextStepAction();
    } else {
      // If we reset to the beginning (idx0 === -1), do not auto-resume
      if (typeof idx0 === 'number' && idx0 < 0) return;
      await sendNextStepAction();
    }
  };

  // Secondary chat input after first submission
  const handleSecondarySend = useCallback(async () => {
    if (loading) return;
    const trimmed = (secondaryInput || '').trim();
    const isPaused = pausedStepIndex >= 0;
    // Allow empty send while paused (simulator-style `msg`)
    if (!trimmed && !isPaused) return;
    if (trimmed) {
      const userMsg = { role: 'user', content: trimmed };
      pushPipelineMessage(userMsg);
    }
    setSecondaryInput('');

    if (pausedStepIndex >= 0) {
      try {
        // While paused, Msg always continues the CURRENT step (even empty).
        await sendMsgAction(trimmed);
      } catch (e) {
        setError(e?.message || 'Failed to continue current step');
      }
    } else {
      // Not paused yet — queue the input for the next resume
      if (trimmed) {
        const userMsg = { role: 'user', content: trimmed };
        setExtraUserInputs(prev => ([...prev, userMsg]));
        pushPipelineMessage({ role: 'system', content: 'Your input will be applied at the next pause.' });
      }
    }
  }, [loading, secondaryInput, pausedStepIndex, sendMsgAction]);

  const [activeResultTab, setActiveResultTab] = useState('extracted'); // 'extracted' | 'files'
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');

  // Extracted Results: company logo preview (static path)
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [logoCacheBuster, setLogoCacheBuster] = useState(() => Date.now());

  // Viewer modal
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewingFilePath, setViewingFilePath] = useState(null);

  const normalizeCompanyDomainUrl = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (/^https?:\/\//i.test(s)) return s;
    return `https://${s}`;
  };

  const isValidCompanyDomainUrl = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return false;
    if (/\s/.test(s)) return false;
    const normalized = normalizeCompanyDomainUrl(s);
    try {
      const u = new URL(normalized);
      if (!u.hostname) return false;
      if (!u.hostname.includes('.')) return false;
      return true;
    } catch {
      return false;
    }
  };

  const companyNameError = !input.companyName.trim() ? 'Company name is required.' : '';
  const companyDomainUrlError = !input.companyDomainUrl.trim()
    ? 'Company domain URL is required.'
    : (!isValidCompanyDomainUrl(input.companyDomainUrl)
      ? 'Enter a website like https://www.cooperparry.com/ or cooperparry.com'
      : '');
  const expectedStartDateError = !input.expectedStartDate.trim() ? 'Expected start date is required.' : '';

  const startPipeline = useCallback(async () => {
    if (!selectedPipeline) { setError('Select a pipeline first.'); return; }
    const companyName = (input.companyName || '').trim();
    const companyDomainUrlRaw = (input.companyDomainUrl || '').trim();
    const companyDomainUrl = normalizeCompanyDomainUrl(companyDomainUrlRaw);
    const meetingNotes = (input.meetingNotes || '').trim();
    const expectedStartDate = (input.expectedStartDate || '').trim();
    if (!companyName) { setError('Please enter a company name.'); return; }
    if (!companyDomainUrlRaw) { setError('Please enter a company domain URL.'); return; }
    if (!isValidCompanyDomainUrl(companyDomainUrlRaw)) { setError('Please enter a valid company domain URL (e.g. https://www.cooperparry.com/ or cooperparry.com).'); return; }
    if (!expectedStartDate) { setError('Please enter an expected start date.'); return; }

    const initial_user_input = `Company Name: ${companyName}\n\nMeeting Notes:\n${meetingNotes}\n\nCompany Domain: ${companyDomainUrl}`;

    setError(''); setLoading(true); setMessages([]);
    setFinalOutput(null); setGeneratedFiles([]); setExtractedEntries([]);
  setLogoLoadFailed(false);
  setLogoCacheBuster(Date.now());

    // Ensure we have the pipeline prompts (so pause UI is accurate)
    try {
      if (!pipelinePrompts || pipelinePrompts.length === 0) {
        try {
          const resp = await fetch(`${BASE_URL}/api/prompt-pipelines/details-by-name/${encodeURIComponent(selectedPipeline)}`, { credentials: 'include' });
          if (resp.ok) {
            const body = await resp.json();
            if (Array.isArray(body)) setPipelinePrompts(body);
          }
        } catch (e) {
          // non-fatal: continue without prompts
          console.warn('Failed to load pipeline prompts for pause UI', e);
        }
      }
      // Pre-assign a chat id so we can request pauses reliably even for step 1
      let preAssignedChatId = chatIdRef.current;
      if (!preAssignedChatId) {
        preAssignedChatId = `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
        chatIdRef.current = preAssignedChatId;
        setChatId(preAssignedChatId);
      }

      const params = {
        date: expectedStartDate,
        company_domain: companyDomainUrl,
      }

      const payload = {
        pipeline_name: selectedPipeline,
        selected_tools: {},
        human_loop_setting: loopsBeforeHumanInput,
        chat_id: preAssignedChatId,
        action: { type: 'start', initial_user_input, params },
      };

      // If user chose pause after step 1, request it before starting so it's guaranteed
      try {
        const target = pauseAfterStepRef.current.includes(1);
        if (target && preAssignedChatId) {
          await requestPauseToServer(preAssignedChatId);
        }
      } catch (e) { /* non-fatal */ }

      const response = await fetch(`${BASE_URL}/api/pipelines/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      if (!response.body) throw new Error('No response body from pipeline execution.');

      // reset shared message buffer
      pipelineMessagesRef.current = [];
      setMessages([]);

      await streamSSE(response, makeSSEHandlers(handleEvent));

      setLoading(false);
    } catch (e) {
      setError(e.message || 'Failed to run pipeline.');
      setLoading(false);
    }
  }, [selectedPipeline, input, loopsBeforeHumanInput]);

  // Load pipeline prompts when the page mounts or when selectedPipeline changes
  useEffect(() => {
    let mounted = true;
    const fetchPrompts = async () => {
      if (!selectedPipeline) return;
      try {
        const resp = await fetch(`${BASE_URL}/api/prompt-pipelines/details-by-name/${encodeURIComponent(selectedPipeline)}`, { credentials: 'include' });
        if (!mounted) return;
        if (resp.ok) {
          const body = await resp.json();
          if (Array.isArray(body)) {
            setPipelinePrompts(body);
            setPauseAfterStep(body.map((_, idx) => idx + 1));
          }
        }
      } catch (e) {
        console.warn('Failed to load prompts for pipeline', selectedPipeline, e);
      }
    };
    fetchPrompts();
    return () => { mounted = false; };
  }, [selectedPipeline]);

  // Trigger backend extraction when new files are found (first 4 only)
  useEffect(() => {
    if (!generatedFiles || generatedFiles.length === 0) return;
    const top4 = generatedFiles.slice(0, 4);
    console.log('Checking for extraction of top 4 generated files:\n', top4);
    top4.forEach((filePath, idx) => {
      const fileNumber = idx + 1;
      if ((extractedEntriesRef.current || []).some(e => e.filePath === filePath)) return;
      setExtractedEntries(prev => ([...prev, { filePath, fileNumber, text: '', loading: true, error: '', parseFailed: false, specialists: undefined, profiles: undefined, selectedIndex: 0, profileExperienceOpen: {}, criteriaOpen: {}, project_goal: '', evaluation_criteria: [], criteriaCollapsed: false }]));
  if (fileNumber === 3) {
        extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl: BASE_URL })
          .then((data) => {
            const specialists = Array.isArray(data?.proposed_specialists) ? data.proposed_specialists : undefined;
            return extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL })
              .then((text) => ({ text, specialists }))
              .catch(() => ({ text: '', specialists }));
          })
          .then(({ text, specialists }) => {
            setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, specialists, loading: false, error: '', parseFailed: false } : e));
          })
          .catch(err => {
            const msg = err?.response?.data?.message || err.message || 'Extraction failed';
            const is500 = isServerError500(err);
            if (is500) {
              notifyWrongFormat(filePath, fileNumber);
              // Fire and forget: attempt to auto-fix structure then re-parse
              attemptAutoFix({ filePath, fileNumber, errorMessage: msg });
            }
            setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: false, error: msg, parseFailed: !!is500 } : e));
          });
      } else {
        if (fileNumber === 2) {
          // For file 2 fetch both structured data and normalized text so we can provide a structured criteria editor
          extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl: BASE_URL })
            .then((data) => {
              const project_goal = data?.project_goal ?? '';
              const rawCriteria = data?.evaluation_criteria ?? data?.criteria ?? data ?? null;

              const normalizeCriteria = (raw) => {
                if (!raw) return [];
                if (Array.isArray(raw)) {
                  return raw.map(item => {
                    if (typeof item === 'string') return { criterion: item, description: '' };
                    if (typeof item === 'object') {
                      // common field names: 'criterion' + 'description' or 'name'/'details'
                      return {
                        criterion: item.criterion ?? item.name ?? item.title ?? (item.criterionText ?? ''),
                        description: item.description ?? item.details ?? item.desc ?? ''
                      };
                    }
                    return { criterion: String(item), description: '' };
                  });
                }
                if (typeof raw === 'object') {
                  if (Array.isArray(raw.criteria)) return normalizeCriteria(raw.criteria);
                  if (Array.isArray(raw.evaluation_criteria)) return normalizeCriteria(raw.evaluation_criteria);
                  // If it's an object with numeric keys, convert to array
                  const arr = [];
                  for (const k of Object.keys(raw)) {
                    const v = raw[k];
                    if (typeof v === 'string') arr.push({ criterion: k, description: v });
                  }
                  if (arr.length) return arr;
                  // last resort: stringify object into a single criterion
                  return [{ criterion: '', description: JSON.stringify(raw) }];
                }
                return [];
              };

              const evaluation_criteria = normalizeCriteria(rawCriteria);

              return extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL })
                .then((text) => {
                  // If evaluation_criteria is empty, try to parse the JSON block from the text
                  let finalCriteria = evaluation_criteria;
                  if ((!finalCriteria || finalCriteria.length === 0) && typeof text === 'string') {
                    try {
                      const s = text.indexOf('{');
                      const e = text.lastIndexOf('}');
                      if (s !== -1 && e !== -1 && e > s) {
                        const js = text.substring(s, e + 1);
                        const p = JSON.parse(js);
                        const arr = p?.criteria ?? p?.evaluation_criteria ?? null;
                        if (Array.isArray(arr)) {
                          finalCriteria = normalizeCriteria(arr);
                        }
                      }
                    } catch (err) {
                      // ignore parse errors
                    }
                  }
                  return ({ text, project_goal, evaluation_criteria: finalCriteria });
                })
                .catch(() => ({ text: '', project_goal, evaluation_criteria }));
            })
            .then(({ text, project_goal, evaluation_criteria }) => {
              setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, project_goal, evaluation_criteria, loading: false, error: '', parseFailed: false, criteriaOpen: e.criteriaOpen || {} } : e));
            })
            .catch(err => {
              const msg = err?.response?.data?.message || err.message || 'Extraction failed';
              const is500 = isServerError500(err);
              if (is500) {
                notifyWrongFormat(filePath, fileNumber);
                attemptAutoFix({ filePath, fileNumber, errorMessage: msg });
              }
              setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: false, error: msg, parseFailed: !!is500 } : e));
            });
        } else if (fileNumber === 4) {
          // File 4: backend uses the same response shape as file 3 (key: `proposed_specialists`),
          // but file 4 is *profiles* (array of objects). IMPORTANT: use raw data, not normalized text
          // (normalized text becomes Markdown and can't be reliably parsed back into JSON).
          Promise.all([
            extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => null),
            extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => ''),
          ])
            .then(([data, text]) => {
              const picked = pickProfilesArrayFromRoot(data);
              const profiles = Array.isArray(picked.profiles) ? picked.profiles : undefined;
              setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, profiles, loading: false, error: '', parseFailed: false, profileExperienceOpen: e.profileExperienceOpen || {} } : e));
            })
            .catch(err => {
              const msg = err?.response?.data?.message || err.message || 'Extraction failed';
              const is500 = isServerError500(err);
              if (is500) {
                notifyWrongFormat(filePath, fileNumber);
                attemptAutoFix({ filePath, fileNumber, errorMessage: msg });
              }
              setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: false, error: msg, parseFailed: !!is500 } : e));
            });
        } else {
          extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL })
            .then((text) => {
              setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, loading: false, error: '', parseFailed: false } : e));
            })
            .catch(err => {
              const msg = err?.response?.data?.message || err.message || 'Extraction failed';
              const is500 = isServerError500(err);
              if (is500) {
                notifyWrongFormat(filePath, fileNumber);
                attemptAutoFix({ filePath, fileNumber, errorMessage: msg });
              }
              setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: false, error: msg, parseFailed: !!is500 } : e));
            });
        }
      }
    });
  }, [generatedFiles]);

  // Keep extracted entries aligned with CURRENT generated files.
  // The Raw Files tab is driven by generatedFiles; Extracted Results should follow that same source of truth.
  useEffect(() => {
    const top4 = Array.isArray(generatedFiles) ? generatedFiles.slice(0, 4) : [];
    if (!top4.length) return;
      setExtractedEntries((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const byPath = new Map(prevArr.map(e => [e.filePath, e]));
      const seen = new Set();

      const aligned = top4.map((filePath, idx) => {
        const fileNumber = idx + 1;
        const existing = byPath.get(filePath);
        seen.add(filePath);
        if (existing) {
          return existing.fileNumber === fileNumber ? existing : { ...existing, fileNumber };
        }
        return { filePath, fileNumber, text: '', loading: true, error: '', parseFailed: false, specialists: undefined, profiles: undefined, selectedIndex: 0, profileExperienceOpen: {}, criteriaOpen: {}, project_goal: '', evaluation_criteria: [], criteriaCollapsed: false };
      });

      // Keep old entries around (they simply won't be rendered).
      const rest = prevArr.filter(e => e && e.filePath && !seen.has(e.filePath));
      return [...aligned, ...rest];
    });
  }, [generatedFiles]);

  // Ensure File 2 entries have evaluation_criteria populated by parsing text if backend didn't provide it
  useEffect(() => {
    if (!extractedEntries || extractedEntries.length === 0) return;
    extractedEntries.forEach((entry) => {
      if (entry.fileNumber !== 2) return;
      const hasCriteria = Array.isArray(entry.evaluation_criteria) && entry.evaluation_criteria.length > 0;
      if (hasCriteria) return;

      const tryParseFromText = (txt) => {
        if (!txt || typeof txt !== 'string') return null;
        // find first { ... } block
        const s = txt.indexOf('{');
        const e = txt.lastIndexOf('}');
        if (s === -1 || e === -1 || e <= s) return null;
        const js = txt.substring(s, e + 1);
        try {
          const parsed = JSON.parse(js);
          // look for criteria array
          const arr = parsed.criteria ?? parsed.evaluation_criteria ?? null;
          if (Array.isArray(arr)) return { parsed, arr };
          // maybe criteria nested under another key
          for (const k of Object.keys(parsed)) {
            if (Array.isArray(parsed[k])) return { parsed, arr: parsed[k] };
          }
          return null;
        } catch (e) {
          return null;
        }
      };

      const parsed = tryParseFromText(entry.text || '');
      if (parsed && parsed.arr) {
        // normalize into {criterion,description}
        const norm = parsed.arr.map(item => {
          if (typeof item === 'string') return { criterion: item, description: '' };
          if (typeof item === 'object') return { criterion: item.criterion ?? item.name ?? item.title ?? '', description: item.description ?? item.details ?? item.desc ?? '' };
          return { criterion: String(item), description: '' };
        });
        setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath ? { ...x, evaluation_criteria: norm, project_goal: x.project_goal || (parsed.parsed?.project_name ?? '') } : x));
      }
    });
  }, [extractedEntries]);

  const outerScrollRef = useRef(null);
  const { containerRef: messagesContainerRef, processed: processedMessages, displayed: displayedMessages, hasMore: hasMoreMessages, loadMore: loadMoreMessages, displayedCount } = useChatDisplay(messages, true, 15, outerScrollRef);
  const markdownOptions = useMemo(() => buildMarkdownOptions({
    authUsername,
    BASE_URL,
    setIsViewerOpen,
    setViewingFilePath,
  }), [authUsername, BASE_URL]);

  const openViewer = useCallback((pathRelativeToHome) => {
    setViewingFilePath(pathRelativeToHome);
    setIsViewerOpen(true);
  }, []);

  const annotatedDisplayedMessages = useMemo(() => annotateAgentSteps(displayedMessages), [displayedMessages]);

  const handleLoadAgentStep = useCallback((step) => {
    if (!Array.isArray(processedMessages) || processedMessages.length === 0) return;
    const msgs = annotateAgentSteps(processedMessages);
    const target = msgs.find(m => m._agentStep === step);
    if (!target) return;
    // Load checkpoint using step index (1-based step provided by button) => backend expects zero-based when fetching checkpoint
    setLoadCheckpointStep(String(step));
  }, [processedMessages]);

  // Re-extract an entry when its underlying file content has changed
  const reExtractEntryByPath = useCallback(async (filePath) => {
    try {
      const entry = (extractedEntriesRef.current || []).find(e => e.filePath === filePath);
      if (!entry) return;
      const fileNumber = entry.fileNumber;
      // set loading for this entry
      setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: true, error: '' } : e));

      if (fileNumber === 3) {
        // Specialists list + text
        const data = await extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => null);
        const specialists = Array.isArray(data?.proposed_specialists) ? data.proposed_specialists : undefined;
        const text = await extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => '');
        setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, specialists, loading: false, error: '', parseFailed: false } : e));
        return;
      }
      if (fileNumber === 2) {
        // Project goal + evaluation criteria (structured)
        const data = await extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => null);
        const project_goal = data?.project_goal ?? '';
        const rawCriteria = data?.evaluation_criteria ?? data?.criteria ?? data ?? null;
        const normalizeCriteria = (raw) => {
          if (!raw) return [];
          if (Array.isArray(raw)) {
            return raw.map(item => {
              if (typeof item === 'string') return { criterion: item, description: '' };
              if (typeof item === 'object') {
                return { criterion: item.criterion ?? item.name ?? item.title ?? (item.criterionText ?? ''), description: item.description ?? item.details ?? item.desc ?? '' };
              }
              return { criterion: String(item), description: '' };
            });
          }
          if (typeof raw === 'object') {
            if (Array.isArray(raw.criteria)) return normalizeCriteria(raw.criteria);
            if (Array.isArray(raw.evaluation_criteria)) return normalizeCriteria(raw.evaluation_criteria);
            const arr = [];
            for (const k of Object.keys(raw)) {
              const v = raw[k];
              if (typeof v === 'string') arr.push({ criterion: k, description: v });
            }
            if (arr.length) return arr;
            return [{ criterion: '', description: JSON.stringify(raw) }];
          }
          return [];
        };
        let evaluation_criteria = normalizeCriteria(rawCriteria);
        const text = await extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => '');
        if ((!evaluation_criteria || evaluation_criteria.length === 0) && typeof text === 'string') {
          try {
            const s = text.indexOf('{');
            const e = text.lastIndexOf('}');
            if (s !== -1 && e !== -1 && e > s) {
              const js = text.substring(s, e + 1);
              const p = JSON.parse(js);
              const arr = p?.criteria ?? p?.evaluation_criteria ?? null;
              if (Array.isArray(arr)) evaluation_criteria = normalizeCriteria(arr);
            }
          } catch {}
        }
        setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, project_goal, evaluation_criteria, loading: false, error: '', parseFailed: false, criteriaOpen: e.criteriaOpen || {} } : e));
        return;
      }

      if (fileNumber === 4) {
        const data = await extractSpecialistProposalFileData({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => null);
        const text = await extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => '');
        const picked = pickProfilesArrayFromRoot(data);
        const profiles = Array.isArray(picked.profiles) ? picked.profiles : undefined;
        setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, profiles, loading: false, error: '', parseFailed: false, profileExperienceOpen: e.profileExperienceOpen || {} } : e));
        return;
      }

      // Default: just refresh plain text
      const text = await extractSpecialistProposalFileText({ filePath, fileNumber, baseUrl: BASE_URL }).catch(() => '');
      setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, text, loading: false, error: '', parseFailed: false } : e));
    } catch (err) {
      const is500 = isServerError500(err);
      if (is500) {
        const num = (extractedEntriesRef.current || []).find(e => e.filePath === filePath)?.fileNumber;
        notifyWrongFormat(filePath, num);
        attemptAutoFix({ filePath, fileNumber: num, errorMessage: err?.message || 'Re-parse failed with 500' });
      }
      setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: false, error: err?.message || 'Re-parse failed', parseFailed: !!is500 } : e));
    }
  }, [BASE_URL]);

  // Attempt to auto-fix malformed output by calling backend fixer and overwriting the file
  const attemptAutoFix = useCallback(async ({ filePath, fileNumber, errorMessage, force = false }) => {
    try {
      const key = `${fileNumber || ''}:${String(filePath || '')}`;
      if (!force && autoFixedRef.current.has(key)) return; // already attempted
      autoFixedRef.current.add(key);

      // Indicate loading for this entry and inform the user
      setExtractedEntries(prev => prev.map(e => e.filePath === filePath ? { ...e, loading: true } : e));
      const shortName = String(filePath || '').split('/').pop();
      pushPipelineMessage({ role: 'system', content: `Attempting to auto-fix file format for ${shortName}…` });

      const original = await readUserFileContent(filePath);
      const resp = await fixFileStructure({ filePath, fileNumber, errorMessage, originalContent: original, baseUrl: BASE_URL });
      // Normalize response to a string content
      let fixed = '';
      if (typeof resp === 'string') {
        fixed = resp;
      } else if (resp && typeof resp === 'object') {
        fixed = resp.fixed_content || resp.content || resp.new_content || resp.text || '';
      }
      if (!fixed || typeof fixed !== 'string') throw new Error('Auto-fix returned no content');

      await overwriteUserFileContent(filePath, fixed);

      const name = String(filePath || '').split('/').pop();
      pushPipelineMessage({ role: 'system', content: `Auto-fixed file format for ${name}. Re-parsing…` });

      // Refresh the extracted view
      await reExtractEntryByPath(filePath);
    } catch (e) {
      const name = String(filePath || '').split('/').pop();
      pushPipelineMessage({ role: 'system', content: `Auto-fix failed for ${name}: ${e?.message || e}` });
      setExtractedEntries(prev => prev.map(ent => ent.filePath === filePath ? { ...ent, loading: false } : ent));
    }
  }, [BASE_URL, readUserFileContent, overwriteUserFileContent, reExtractEntryByPath]);

  const scheduleReExtractForPaths = useCallback((paths) => {
    if (!Array.isArray(paths) || paths.length === 0) return;
    const setPaths = new Set(paths.map(p => normalizeToHomeRelative(p)));
    const toRefresh = (extractedEntriesRef.current || [])
      .filter(e => setPaths.has(e.filePath))
      .slice(0, 4);
    toRefresh.forEach(e => { reExtractEntryByPath(e.filePath); });
  }, [reExtractEntryByPath]);

  const chatMaxHeight = '100%';
  const chatMinHeight = '0';

  // Build a compact status chip for the chat header
  const internalPaused = isInternalPauseRef.current === true;
  const pausedStepDisplay = (pausedStepIndex >= 0)
    ? (internalPaused ? (pausedStepIndex + 1) : (lastStepCompletedRef.current || Math.max(1, pausedStepIndex)))
    : null;
  const chipEl = loading
    ? (
      <span className="status-chip running">Running…</span>
    )
    : (pausedStepIndex >= 0 ? (
      <span
        className="status-chip paused"
        title={internalPaused ? 'Paused within a step; use Msg to continue or Next Step to skip' : 'Paused after a step; use Msg to stay on this step or Next Step to advance'}
      >
        {internalPaused
          ? `Paused (in-step${pausedStepDisplay ? ` · Step ${pausedStepDisplay}` : ''})`
          : `Paused (after step${pausedStepDisplay ? ` ${pausedStepDisplay}` : ''})`}
      </span>
    ) : null);

  const renderFilesList = () => {
    if (!generatedFiles || generatedFiles.length === 0) {
      return <div className="empty-state">No files were reported by the pipeline.</div>;
    }
    return (
      <div className="files-list">
        {generatedFiles.map((p, idx) => {
          const rel = String(p || '').replace(/^Home\//, '');
          const display = rel.split('/').pop();
          return (
            <div key={idx} className="file-item">
              <button className="linklike" onClick={() => openViewer(rel)} title={rel}>{display}</button>
            </div>
          );
        })}
      </div>
    );
  };

  const canFinalize = useMemo(() => Array.isArray(generatedFiles) && generatedFiles.slice(0, 4).length === 4, [generatedFiles]);

  const onFinalize = async () => {
    if (!canFinalize || finalizing) return;
    try {
      setFinalizeError('');
      setFinalizing(true);
      const files = generatedFiles.slice(0, 4);
      const result = await finalizeSpecialistProposal({ files, baseUrl: BASE_URL });
      // Accept multiple response shapes: string, { pptx_path }, { file_path }, { path }
      let filePath = '';
      if (typeof result === 'string') {
        filePath = result;
      } else if (result && typeof result === 'object') {
        filePath = result.pptx_path || result.file_path || result.path || '';
      }
      setMessages(prev => ([...prev, { role: 'system', content: 'Finalization requested successfully.' }]));

      if (filePath) {
        // Normalize to path relative to Home (backend download route expects that)
        let relative = String(filePath);
        const idx = relative.indexOf('/Home/');
        if (idx !== -1) {
          relative = relative.substring(idx + '/Home/'.length);
        } else {
          relative = relative.replace(/^Home\//, '');
        }
        const fileName = relative.split('/').pop();
        // const encoded = encodeURIComponent(relative);
        // Use file_explorer download route
        // Add a cache-busting timestamp query param so browser never serves a cached copy
        const cacheBuster = `cb=${Date.now()}`;
        const downloadUrl = `${BASE_URL}/api/file_explorer/download_user_file/${encodeURIComponent(relative)}?${cacheBuster}`;
        try {
          // Use native fetch to get raw bytes; include credentials for session auth
          const resp = await fetch(downloadUrl, {
            method: 'GET',
            credentials: 'include',
            // Ask the browser and any intermediate caches to always fetch a fresh copy
            cache: 'no-store',
            headers: {
              'Accept': 'application/vnd.openxmlformats-officedocument.presentationml.presentation, application/octet-stream, */*'
            }
          });
          if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);

          const contentType = resp.headers.get('content-type') || '';

          let blob = null;
          if (contentType.indexOf('application/json') !== -1) {
            // Backend returned JSON (maybe base64 payload). Try to parse.
            const j = await resp.json();
            // Look for any top-level base64-like string values
            const candidate = (typeof j === 'string' ? j : (j.pptx || j.data || j.content || j.file || j.payload || j.base64 || j.blob));
            if (candidate && typeof candidate === 'string') {
              // If this looks like base64 (starts with UEsDB or similar), decode
              const b64 = candidate.trim();
              const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
              blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
            } else {
              throw new Error('JSON download did not contain binary payload');
            }
          } else if (contentType.startsWith('text/') ) {
            // Sometimes server returns base64 as text/plain
            const txt = await resp.text();
            const maybe = txt.trim();
            // Heuristic: base64 ZIPs often start with 'UEsDB'
            if (/^[A-Za-z0-9+/=\s]+$/.test(maybe) && maybe.length > 100 && maybe.indexOf('UEs') === 0) {
              const bytes = Uint8Array.from(atob(maybe), c => c.charCodeAt(0));
              blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
            } else {
              throw new Error('Text download not recognized as base64 binary');
            }
          } else {
            // Assume binary; use arrayBuffer
            const ab = await resp.arrayBuffer();
            blob = new Blob([ab], { type: contentType || 'application/octet-stream' });
          }

          // Trigger download
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName || 'download.pptx';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

        } catch (e) {
          console.warn('Robust download failed, falling back to navigation', e);
          try {
            const link = document.createElement('a');
            // Use same cache-busted URL for the fallback navigation method
            link.href = `${BASE_URL}/api/file_explorer/download_user_file/${encodeURIComponent(relative)}?${cacheBuster}`;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } catch (err) {
            console.warn('Final download fallback failed, opening in new tab', err);
            window.open(`${BASE_URL}/api/file_explorer/download_user_file/${encodeURIComponent(relative)}?${cacheBuster}`, '_blank', 'noopener');
          }
        }
      }
    } catch (e) {
      setFinalizeError(e?.response?.data?.message || e.message || 'Failed to finalize');
    } finally {
      setFinalizing(false);
    }
  };

  const renderSpecialists = (entry) => {
    const list = Array.isArray(entry.specialists) ? entry.specialists : [];
    if (!list.length) return <div className="empty-state">No specialists found.</div>;
    const currentIndex = entry.selectedIndex ?? 0;
    const selected = list[currentIndex] || list[0];
    const setSelectedIndex = (i) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath ? { ...x, selectedIndex: i } : x));

    const updateSpecialistAt = (idx, patch) => {
      setExtractedEntries(prev => prev.map(x => {
        if (x.filePath !== entry.filePath) return x;
        const arr = Array.isArray(x.specialists) ? x.specialists : [];
        const next = arr.map((s, i) => i === idx ? ({ ...(s || {}), ...(patch || {}) }) : s);
        return { ...x, specialists: next };
      }));
    };

    const skills = Array.isArray(selected?.["relevant skills"]) ? selected["relevant skills"] : [];
    return (
      <div className="specialists-wrap">
        <div className="names-list">
          {list.map((s, i) => (
            <button key={i} className={`name-item ${i === currentIndex ? 'selected' : ''}`} onClick={() => setSelectedIndex(i)}>
              {s?.name || `Specialist ${i + 1}`}
            </button>
          ))}
        </div>
        <div className="details-card specialists-editor">
          <div className="detail-row"><strong>Name:</strong>{' '}
            <input
              type="text"
              value={selected?.name ?? ''}
              onChange={(e) => updateSpecialistAt(currentIndex, { name: e.target.value })}
            />
          </div>
          <div className="detail-row"><strong>Availability:</strong>{' '}
            <input
              type="text"
              value={selected?.availability ?? ''}
              onChange={(e) => updateSpecialistAt(currentIndex, { availability: e.target.value })}
            />
          </div>
          <div className="detail-row"><strong>Location:</strong>{' '}
            <input
              type="text"
              value={selected?.location ?? ''}
              onChange={(e) => updateSpecialistAt(currentIndex, { location: e.target.value })}
            />
          </div>
          <div className="detail-row"><strong>Working Pattern:</strong>{' '}
            <input
              type="text"
              value={selected?.["working pattern"] ?? selected?.working_pattern ?? ''}
              onChange={(e) => updateSpecialistAt(currentIndex, { 'working pattern': e.target.value, working_pattern: e.target.value })}
            />
          </div>
          <div className="detail-row"><strong>Standard Hourly Rate:</strong>{' '}
            <input
              type="text"
              value={selected?.["standard hourly rate"] ?? selected?.hourly_rate ?? ''}
              onChange={(e) => updateSpecialistAt(currentIndex, { 'standard hourly rate': e.target.value, hourly_rate: e.target.value })}
            />
          </div>
          <div className="detail-row"><strong>Relevant Skills:</strong>
            <div className="chips">
              {skills.length ? skills.map((sk, idx) => (<span key={idx} className="chip">{sk}</span>)) : '—'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSpecialistProfiles = (entry) => {
    const list = Array.isArray(entry.profiles) ? entry.profiles : [];
    if (!list.length) {
      return <div className="empty-state">No specialist profiles found.</div>;
    }

    const currentIndex = entry.selectedIndex ?? 0;
    const selected = list[currentIndex] || list[0];
    const setSelectedIndex = (i) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath ? { ...x, selectedIndex: i } : x));

    const updateProfileAt = (idx, patch) => {
      setExtractedEntries(prev => prev.map(x => {
        if (x.filePath !== entry.filePath) return x;
        const arr = Array.isArray(x.profiles) ? x.profiles : [];
        const next = arr.map((p, i) => i === idx ? ({ ...(p || {}), ...(patch || {}) }) : p);
        return { ...x, profiles: next };
      }));
    };

    const updateExperienceAt = (profileIdx, expIdx, patch) => {
      setExtractedEntries(prev => prev.map(x => {
        if (x.filePath !== entry.filePath) return x;
        const arr = Array.isArray(x.profiles) ? x.profiles : [];
        const next = arr.map((p, i) => {
          if (i !== profileIdx) return p;
          const cur = (p && typeof p === 'object') ? p : {};
          const exp = Array.isArray(cur.relevant_experience) ? cur.relevant_experience : [];
          const nextExp = exp.map((e, j) => j === expIdx ? ({ ...(e || {}), ...(patch || {}) }) : e);
          return { ...cur, relevant_experience: nextExp };
        });
        return { ...x, profiles: next };
      }));
    };

    const addExperienceRow = (profileIdx) => {
      setExtractedEntries(prev => prev.map(x => {
        if (x.filePath !== entry.filePath) return x;
        const arr = Array.isArray(x.profiles) ? x.profiles : [];
        const next = arr.map((p, i) => {
          if (i !== profileIdx) return p;
          const cur = (p && typeof p === 'object') ? p : {};
          const exp = Array.isArray(cur.relevant_experience) ? cur.relevant_experience : [];
          const nextExp = [...exp, { company_name: '', role_name: '', start_date: '', end_date: '' }];
          return {
            ...cur,
            relevant_experience: nextExp,
          };
        });
        // Open the newly-added row by default
        const nextIdx = (() => {
          try {
            const prof = next[profileIdx];
            const exp = Array.isArray(prof?.relevant_experience) ? prof.relevant_experience : [];
            return Math.max(0, exp.length - 1);
          } catch { return 0; }
        })();
        const openMap = (x.profileExperienceOpen && typeof x.profileExperienceOpen === 'object') ? x.profileExperienceOpen : {};
        const byProfile = (openMap[profileIdx] && typeof openMap[profileIdx] === 'object') ? openMap[profileIdx] : {};
        const updated = { ...openMap, [profileIdx]: { ...byProfile, [nextIdx]: true } };
        return { ...x, profiles: next, profileExperienceOpen: updated };
      }));
    };

    const deleteExperienceRow = (profileIdx, expIdx) => {
      setExtractedEntries(prev => prev.map(x => {
        if (x.filePath !== entry.filePath) return x;
        const arr = Array.isArray(x.profiles) ? x.profiles : [];
        const next = arr.map((p, i) => {
          if (i !== profileIdx) return p;
          const cur = (p && typeof p === 'object') ? p : {};
          const exp = Array.isArray(cur.relevant_experience) ? cur.relevant_experience : [];
          return { ...cur, relevant_experience: exp.filter((_, j) => j !== expIdx) };
        });
        // Also remove the open state for that row (and shift indices > expIdx down by 1)
        const openMap = (x.profileExperienceOpen && typeof x.profileExperienceOpen === 'object') ? x.profileExperienceOpen : {};
        const byProfile = (openMap[profileIdx] && typeof openMap[profileIdx] === 'object') ? openMap[profileIdx] : {};
        const shifted = {};
        Object.keys(byProfile).forEach((k) => {
          const n = Number(k);
          if (Number.isNaN(n)) return;
          if (n === expIdx) return;
          const nextKey = n > expIdx ? n - 1 : n;
          shifted[nextKey] = !!byProfile[k];
        });
        const updated = { ...openMap, [profileIdx]: shifted };
        return { ...x, profiles: next, profileExperienceOpen: updated };
      }));
    };

    const isExperienceOpen = (profileIdx, expIdx) => {
      const openMap = (entry.profileExperienceOpen && typeof entry.profileExperienceOpen === 'object') ? entry.profileExperienceOpen : {};
      const byProfile = (openMap[profileIdx] && typeof openMap[profileIdx] === 'object') ? openMap[profileIdx] : {};
      return !!byProfile[expIdx];
    };

    const toggleExperienceOpen = (profileIdx, expIdx) => {
      setExtractedEntries(prev => prev.map(x => {
        if (x.filePath !== entry.filePath) return x;
        const openMap = (x.profileExperienceOpen && typeof x.profileExperienceOpen === 'object') ? x.profileExperienceOpen : {};
        const byProfile = (openMap[profileIdx] && typeof openMap[profileIdx] === 'object') ? openMap[profileIdx] : {};
        const next = { ...openMap, [profileIdx]: { ...byProfile, [expIdx]: !byProfile[expIdx] } };
        return { ...x, profileExperienceOpen: next };
      }));
    };

    const idx = Math.max(0, Math.min(currentIndex, list.length - 1));
    const name = safeNameFromProfile(selected, idx);
    const profileText = selected?.profile ?? '';
    const experience = Array.isArray(selected?.relevant_experience) ? selected.relevant_experience : [];

    return (
      <div className="specialists-wrap profiles-wrap">
        <div className="names-list">
          {list.map((p, i) => (
            <button
              key={i}
              className={`name-item ${i === currentIndex ? 'selected' : ''}`}
              onClick={() => setSelectedIndex(i)}
              title={safeNameFromProfile(p, i)}
            >
              {safeNameFromProfile(p, i)}
            </button>
          ))}
        </div>
        <div className="details-card profiles-editor">
          <div className="detail-row"><strong>Name:</strong>{' '}
            <input
              type="text"
              value={name}
              onChange={(e) => updateProfileAt(idx, { name: e.target.value })}
            />
          </div>
          <div className="detail-row">
            <label><strong>Profile</strong></label>
            <textarea
              className="profile-textarea"
              value={typeof profileText === 'string' ? profileText : String(profileText || '')}
              onChange={(e) => updateProfileAt(idx, { profile: e.target.value })}
              rows={8}
            />
          </div>

          <div className="detail-row">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <label><strong>Relevant Experience</strong></label>
              <button className="small" type="button" onClick={() => addExperienceRow(idx)}>Add</button>
            </div>
            <div className="experience-list">
              {experience.length ? experience.map((ex, exIdx) => (
                <div key={exIdx} className="experience-item">
                  <div className="experience-summary">
                    <button
                      type="button"
                      className="experience-toggle"
                      onClick={() => toggleExperienceOpen(idx, exIdx)}
                      title={isExperienceOpen(idx, exIdx) ? 'Collapse' : 'Expand'}
                    >
                      {isExperienceOpen(idx, exIdx) ? '▾' : '▸'}
                    </button>
                    <div className="experience-summary-text" onClick={() => toggleExperienceOpen(idx, exIdx)} title="Click to expand">
                      <strong>{(ex?.company_name || 'Untitled role')}</strong>
                      {ex?.role_name ? ` — ${ex.role_name}` : ''}
                    </div>
                  </div>

                  {isExperienceOpen(idx, exIdx) ? (
                    <>
                      <div className="experience-actions">
                        <button className="small delete-experience" type="button" onClick={() => deleteExperienceRow(idx, exIdx)}>Delete</button>
                      </div>
                      <div className="experience-row">
                        <label className="experience-label">Company</label>
                        <input
                          type="text"
                          value={ex?.company_name ?? ''}
                          onChange={(e) => updateExperienceAt(idx, exIdx, { company_name: e.target.value })}
                        />
                      </div>
                      <div className="experience-row">
                        <label className="experience-label">Role</label>
                        <input
                          type="text"
                          value={ex?.role_name ?? ''}
                          onChange={(e) => updateExperienceAt(idx, exIdx, { role_name: e.target.value })}
                        />
                      </div>
                      <div className="experience-row">
                        <label className="experience-label">Start</label>
                        <input
                          type="text"
                          value={ex?.start_date ?? ''}
                          onChange={(e) => updateExperienceAt(idx, exIdx, { start_date: e.target.value })}
                        />
                        <label className="experience-label">End</label>
                        <input
                          type="text"
                          value={ex?.end_date ?? ''}
                          onChange={(e) => updateExperienceAt(idx, exIdx, { end_date: e.target.value })}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              )) : (
                <div className="empty-state">No experience rows yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle the provided XLSX file and save it as "Specialist_Availability.xlsx" in "./Specialists_Proposals"
  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const filename = (file.name || '').toLowerCase();
    if (!filename.endsWith('.xlsx')) {
      console.error('Invalid file type. Please upload a .xlsx file.');
      // Reset the input so the same file can be re-selected after an error.
      try { event.target.value = ''; } catch {}
      return;
    }

    // save the file to backend
    const uploadUrl = `${BASE_URL}/api/file_explorer/upload_user_files`;
    const formData = new FormData();
    // create a new File with the same content but the desired filename
    const renamedFile = new File(
      [file],
      'Specialist_Availability.xlsx',
      { type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );
    formData.append('files', renamedFile);
    formData.append('relativePath', 'Specialists_Proposals');
    try {
      const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      });
      if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
      const result = await response.json();
      console.log('File uploaded successfully:', result);
    } catch (error) {
      console.error('Error uploading file:', error);
    }

    // after saving the .xlsx file, call the api to save a json version
    reformatSpecialistProposalAvailabilityFile({ filePath: 'Specialists_Proposals/Specialist_Availability.xlsx', baseUrl: BASE_URL })
  };

  const saveChat = () => {
      setLoading(true);
      console.log(`Attempting to save chat...`);
      const promptNameToSave = "SPA_HM_Custom";
      const chatName = input[0];
      // save the file names as additional context in the chat history
      const addedContext = Array.isArray(generatedFiles) ? generatedFiles.slice(0, 4) : [];
      axios.post(`${BASE_URL}/api/save-chat-history`, { messages: messages, prompt_name: promptNameToSave, added_context: addedContext, chat_name: chatName }, { withCredentials: true })
          .then(response => {
              console.log("Chat saved successfully, backend assigned new entry.");
              setLoading(false);
          })
          .catch(err => {
              console.error("Failed to save chat:", err);
              const errorMsg = err.response?.data?.error || err.message || "Could not save the chat.";
              if (err.response?.status === 401) {
                  setError("Authentication failed. Please log in again.");
                  if (handleLogout) handleLogout();
              } else { setError(`Failed to save chat: ${errorMsg}`); }
              setLoading(false);
          });
  };

  const loadPipelineHistory = (chatId) => {
      setLoading(true);
      console.log(`Attempting to load pipeline for chat ID: ${chatId}...`);
      // use the loadSpecialistProposalPipeline function to load the pipeline
      loadSpecialistProposalChatHistory({ chatId, baseUrl: BASE_URL })
          .then((data) => {
              console.log("Pipeline loaded successfully.");
              loadPipelineMessages(data.chat_history);
              applyBackendFiles(data.files);
              setHasInitialSubmitted(true);
              // set the pause state to the last step completed (step 4)
              // Backend pause indexes are 0-based; keep this as a UI convenience state.
              setPausedStepIndex(3);
              // ensure resume/rerun/checkpoint features have necessary state
              lastStepStartedRef.current = 4;
              lastStepCompletedRef.current = 4;
              pauseTypeRef.current = 'checkpoint';
              isInternalPauseRef.current = false;
              setPausedPipelineName(selectedPipeline || '');
              setLoading(false);
          })
          .catch(err => {
              console.error("Failed to load pipeline:", err);
              const errorMsg = err.response?.data?.error || err.message || "Could not load the pipeline.";
              if (err.response?.status === 401) {
                  setError("Authentication failed. Please log in again.");
                  if (handleLogout) handleLogout();
              } else { setError(`Failed to load pipeline: ${errorMsg}`); }
              setLoading(false);
          });

      const loadPipelineMessages = async (payload) => {
        try {
          let history = (payload && payload.chat_history) || [];
          if (typeof history === 'string') { try { history = JSON.parse(history); } catch { /* ignore */ } }
          if (!Array.isArray(history)) history = [];
          const cleaned = history.map(m => ({ role: String(m.role || ''), content: String(m.content || '') }));
          setMessages(cleaned);
          pipelineMessagesRef.current = cleaned;
          if (payload && payload.id) setChatId(payload.id);
        } catch (e) {
          console.error('Failed to load chat history by id:', e);
          setError('Failed to load chat history.');
        }
      }
  };

  const renderExtracted = () => {
    if (!extractedEntries || extractedEntries.length === 0) {
      return <div className="empty-state">No extracted results yet.</div>;
    }

    const top4Files = Array.isArray(generatedFiles) ? generatedFiles.slice(0, 4) : [];
    const byPath = new Map((extractedEntries || []).map(e => [e.filePath, e]));
    const entriesToRender = top4Files.length
      ? top4Files.map((p, idx) => {
          const existing = byPath.get(p);
          const fileNumber = idx + 1;
          return existing
            ? { ...existing, fileNumber }
            : { filePath: p, fileNumber, text: '', loading: true, error: '', parseFailed: false, specialists: undefined, profiles: undefined, selectedIndex: 0, profileExperienceOpen: {}, criteriaOpen: {}, project_goal: '', evaluation_criteria: [], criteriaCollapsed: false };
        })
      : extractedEntries.slice(0, 4);

    const fileNumberToTitle = {
      1: "Company Research",
      2: "Project goal",
      3: "Proposed specialists",
      4: "Proposed specialist profiles",
    };

    const handleUpdateFileContent = async (entry) => {
      // mark as loading
      setExtractedEntries(prev => prev.map(e => e.filePath === entry.filePath ? { ...e, loading: true, error: '' } : e));

      const updateUrl = `${BASE_URL}/api/file_explorer/update_txt_file`;
      const readUrl = `${BASE_URL}/api/file_explorer/read_user_files`;

      let lastError = null;
      let saved = false;

      // Helper to POST to read_user_files and get raw file text
      const fetchOriginal = async (path) => {
        try {
          const resp = await fetch(readUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ filePaths: [path] }),
          });
          if (!resp.ok) throw new Error(`Read failed: ${resp.status}`);
          const j = await resp.json();
          return j?.combinedContent ?? '';
        } catch (e) {
          return null;
        }
      };

      try {
        // Try to fetch original file content so we can patch only the extracted portion
        const original = await fetchOriginal(entry.filePath);

        let newFullContent = entry.text || '';

        if (original !== null && (entry.fileNumber === 1 || entry.fileNumber === 2 || entry.fileNumber === 3 || entry.fileNumber === 4)) {
          // We will attempt to patch the original file by replacing the JSON/preamble parts only.
          try {
            if (entry.fileNumber === 1) {
              // Replace company_research inside the first JSON object in the file
              const start = original.indexOf('{');
              const end = original.lastIndexOf('}');
              if (start !== -1 && end !== -1 && end > start) {
                const pre = original.substring(0, start);
                const post = original.substring(end + 1);
                const jsonStr = original.substring(start, end + 1);
                let obj = {};
                try { obj = JSON.parse(jsonStr); } catch (e) { obj = {}; }
                // set company_research to the edited text
                obj.company_research = entry.text || '';
                const newJson = JSON.stringify(obj, null, 2);
                newFullContent = pre + newJson + post;
              } else {
                // fallback: overwrite whole file with edited text
                newFullContent = entry.text || '';
              }
            } else if (entry.fileNumber === 2) {
              // File 2: pre-JSON project goal (preamble) and JSON criteria
              const start = original.indexOf('{');
              const end = original.lastIndexOf('}');
              if (start !== -1 && end !== -1 && end > start) {
                  const jsonStr = original.substring(start, end + 1);
                  let parsed = null;
                  try { parsed = JSON.parse(jsonStr); } catch (e) { parsed = null; }

                  // Prefer structured fields if present (project_goal / evaluation_criteria)
                  const goal = typeof entry.project_goal === 'string' && entry.project_goal.trim().length ? entry.project_goal.trim() : (entry.text ? entry.text.trim() : '');
                  const newCriteria = Array.isArray(entry.evaluation_criteria) ? entry.evaluation_criteria : [];

                  let outJson = null;
                  if (parsed && typeof parsed === 'object') {
                    // If parsed has 'criteria' or 'evaluation_criteria' keys prefer those
                    if (Object.prototype.hasOwnProperty.call(parsed, 'criteria')) {
                      outJson = { ...parsed, criteria: newCriteria };
                    } else if (Object.prototype.hasOwnProperty.call(parsed, 'evaluation_criteria')) {
                      outJson = { ...parsed, evaluation_criteria: newCriteria };
                    } else if (Object.prototype.hasOwnProperty.call(parsed, 'project_name') || Object.prototype.hasOwnProperty.call(parsed, 'criteria')) {
                      // preserve project_name etc.
                      outJson = { ...parsed, criteria: newCriteria };
                    } else {
                      // fallback: put into 'criteria'
                      outJson = { ...parsed, criteria: newCriteria };
                    }
                  } else {
                    // parsed failed; construct a reasonable object
                    outJson = { project_name: goal || '', criteria: newCriteria };
                  }

                  const pre = original.substring(0, start);
                  const post = original.substring(end + 1);
                  const newJson = JSON.stringify(outJson, null, 2);
                  // Construct: project goal (with a newline) followed by the JSON
                  const newContent = (goal || '') + '\n' + newJson;
                  newFullContent = pre ? pre.replace(pre.trim(), goal) + newJson + post : newContent;
                  if (!pre || pre.trim().length === 0) newFullContent = newContent;
              } else {
                newFullContent = entry.text || '';
              }
            } else if (entry.fileNumber === 3) {
              // Replace the first JSON array found in the file
              const start = original.indexOf('[');
              const end = original.lastIndexOf(']');
              if (start !== -1 && end !== -1 && end > start) {
                const pre = original.substring(0, start);
                const post = original.substring(end + 1);
                // Prefer to use structured specialists if available
                let arr = entry.specialists;
                if (!Array.isArray(arr)) {
                  try { arr = JSON.parse(entry.text || '[]'); } catch (e) { arr = entry.specialists ?? []; }
                }
                const newJson = JSON.stringify(arr, null, 2);
                newFullContent = pre + newJson + post;
              } else {
                newFullContent = entry.text || '';
              }
            } else if (entry.fileNumber === 4) {
              // File 4: patch the JSON specialist profiles without overwriting the file with a flattened text blob
              const currentProfiles = Array.isArray(entry.profiles) ? entry.profiles : null;
              if (!currentProfiles) {
                throw new Error('Cannot save: no parsed profiles available (JSON parse failed).');
              }

              const cleaned = stripCodeFences(original);
              const jsonSub = extractLikelyJsonSubstring(cleaned);
              if (!jsonSub) {
                throw new Error('Cannot save: could not locate a JSON object/array in the original file.');
              }

              let parsed = null;
              try { parsed = JSON.parse(jsonSub); } catch { parsed = null; }
              if (!parsed) {
                throw new Error('Cannot save: original file JSON could not be parsed.');
              }

              const picked = pickProfilesArrayFromRoot(parsed);
              let nextRoot = parsed;
              if (picked.rootShape === 'array') {
                nextRoot = currentProfiles;
              } else {
                // object root: try to update the same container key if present; otherwise default to `profiles`
                const ck = picked.containerKey;
                if (ck && ck.endsWith('.__nested__')) {
                  const base = ck.replace('.__nested__', '');
                  const nested = nextRoot[base];
                  if (nested && typeof nested === 'object') {
                    if (Array.isArray(nested.profiles)) nested.profiles = currentProfiles;
                    else if (Array.isArray(nested.specialist_profiles)) nested.specialist_profiles = currentProfiles;
                    else if (Array.isArray(nested.proposed_specialist_profiles)) nested.proposed_specialist_profiles = currentProfiles;
                    else if (Array.isArray(nested.items)) nested.items = currentProfiles;
                    else nested.profiles = currentProfiles;
                    nextRoot[base] = nested;
                  } else {
                    nextRoot.profiles = currentProfiles;
                  }
                } else if (ck && typeof ck === 'string' && Object.prototype.hasOwnProperty.call(nextRoot, ck)) {
                  nextRoot = { ...nextRoot, [ck]: currentProfiles };
                } else if (Object.prototype.hasOwnProperty.call(nextRoot, 'profiles')) {
                  nextRoot = { ...nextRoot, profiles: currentProfiles };
                } else {
                  // safest fallback: write to `profiles`
                  nextRoot = { ...nextRoot, profiles: currentProfiles };
                }
              }

              const start = cleaned.indexOf(jsonSub);
              const end = start + jsonSub.length;
              if (start === -1) {
                // fallback: treat as whole file content
                newFullContent = JSON.stringify(nextRoot, null, 2);
              } else {
                const pre = cleaned.slice(0, start);
                const post = cleaned.slice(end);
                const newJson = JSON.stringify(nextRoot, null, 2);
                newFullContent = pre + newJson + post;
              }
            }
          } catch (e) {
            // non-fatal, fall back to overwriting whole file
            // For file 4 this would break JSON; so we stop and surface the error.
            if (entry.fileNumber === 4) {
              throw e;
            }
            newFullContent = entry.text || '';
          }
        }

        // Send the reconstructed full file content to the existing update endpoint
        const payload = { filePath: entry.filePath, newContent: newFullContent };
        const resp = await fetch(`${updateUrl}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        if (resp.ok) {
          saved = true;
        } else {
          const txt = await resp.text().catch(() => '');
          lastError = `Status ${resp.status}: ${txt}`;
        }
      } catch (err) {
        lastError = err.message || String(err);
      }

      if (saved) {
        setMessages(prev => ([...prev, { role: 'system', content: `Saved changes for ${String(entry.filePath || '').split('/').pop()}` }]));
        // Re-run parsing for the updated file to refresh Extracted Results
        try {
          await reExtractEntryByPath(entry.filePath);
        } catch (e) {
          setExtractedEntries(prev => prev.map(e => e.filePath === entry.filePath ? { ...e, loading: false, error: e?.message || '' } : e));
        }
      } else {
        setExtractedEntries(prev => prev.map(e => e.filePath === entry.filePath ? { ...e, loading: false, error: lastError || 'Save failed' } : e));
      }
    };

    return (
      <div className="extracted-container">
      {entriesToRender.map((entry) => (
        <React.Fragment key={entry.filePath}>
        {entry.fileNumber === 1 ? (
          <div key={`${entry.filePath}:logo`} className="extracted-entry">
            <div className="extracted-entry-header">
              <strong>Company Logo:</strong> logo.png
              <p>Retrieved using Logo.dev: https://www.logo.dev </p>
            </div>
            <div className="logo-card">
              {!logoLoadFailed ? (
                <img
                  className="logo-preview"
                  alt="Company logo"
                  src={`${BASE_URL}/api/file_explorer/download_user_file/${encodeURIComponent('Specialists_Proposals/logo.png')}?cb=${logoCacheBuster}`}
                  onError={() => setLogoLoadFailed(true)}
                />
              ) : (
                <div className="empty-state">Logo not found at Home/Specialists_Proposals/logo.png</div>
              )}
            </div>
          </div>
        ) : null}

        <div className="extracted-entry">
        <div className="extracted-entry-header">
          <strong>{fileNumberToTitle[entry.fileNumber] || `File ${entry.fileNumber}`}:</strong> {String(entry.filePath || '').split('/').pop()}
        </div>
        {entry.loading ? (
          <div className="loading">Extracting...</div>
        ) : entry.error ? (
          <div className="error" style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <span>{entry.error}</span>
            {entry.parseFailed ? (
              <button
                className="small"
                title="Attempt to auto-fix the file format"
                onClick={() => attemptAutoFix({ filePath: entry.filePath, fileNumber: entry.fileNumber, errorMessage: entry.error, force: true })}
                disabled={entry.loading}
              >
                Auto-fix format
              </button>
            ) : null}
          </div>
        ) : (
          <>
          {entry.fileNumber === 3 && Array.isArray(entry.specialists) ? (
            renderSpecialists(entry)
          ) : entry.fileNumber === 4 && Array.isArray(entry.profiles) ? (
            renderSpecialistProfiles(entry)
          ) : entry.fileNumber === 2 ? (
            <div className="file2-structured">
              <textarea
                value={entry.project_goal ?? entry.text ?? ''}
                onChange={(e) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath ? { ...x, project_goal: e.target.value, text: e.target.value } : x))}
                className="prose-textarea"
                rows={6}
              />
              <div className="criteria-header-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px'}}>
                <label><strong>Evaluation Criteria</strong></label>
                <button
                  className="small"
                  type="button"
                  onClick={() => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath
                    ? { ...x, evaluation_criteria: [...(x.evaluation_criteria||[]), { criterion: '', description: '' }] }
                    : x
                  ))}
                >
                  Add Criterion
                </button>
              </div>

              <div className="criteria-list">
                {(entry.evaluation_criteria || []).length ? (
                  (entry.evaluation_criteria || []).map((c, i) => {
                    const openMap = (entry.criteriaOpen && typeof entry.criteriaOpen === 'object') ? entry.criteriaOpen : {};
                    const isOpen = !!openMap[i];
                    const title = (c?.criterion ?? '').trim() || `Criterion ${i + 1}`;
                    const preview = String(c?.description ?? '').split('\n').join(' ').trim();
                    const shortPreview = preview.length > 120 ? `${preview.slice(0, 120)}…` : preview;

                    const toggle = () => setExtractedEntries(prev => prev.map(x => {
                      if (x.filePath !== entry.filePath) return x;
                      const cur = (x.criteriaOpen && typeof x.criteriaOpen === 'object') ? x.criteriaOpen : {};
                      return { ...x, criteriaOpen: { ...cur, [i]: !cur[i] } };
                    }));

                    const del = () => setExtractedEntries(prev => prev.map(x => {
                      if (x.filePath !== entry.filePath) return x;
                      const nextArr = (x.evaluation_criteria || []).filter((_, idx) => idx !== i);
                      // also shift open-state down
                      const cur = (x.criteriaOpen && typeof x.criteriaOpen === 'object') ? x.criteriaOpen : {};
                      const shifted = {};
                      Object.keys(cur).forEach((k) => {
                        const n = Number(k);
                        if (Number.isNaN(n)) return;
                        if (n === i) return;
                        const nextKey = n > i ? n - 1 : n;
                        shifted[nextKey] = !!cur[k];
                      });
                      return { ...x, evaluation_criteria: nextArr, criteriaOpen: shifted };
                    }));

                    return (
                      <div key={i} className="criteria-item">
                        <div className="criteria-summary">
                          <button type="button" className="criterion-toggle" onClick={toggle} title={isOpen ? 'Collapse' : 'Expand'}>
                            {isOpen ? '▾' : '▸'}
                          </button>
                          <div className="criteria-summary-text" onClick={toggle} title="Click to expand">
                            <strong>{title}</strong>
                            {shortPreview ? <div className="criteria-preview">{shortPreview}</div> : null}
                          </div>
                        </div>

                        {isOpen ? (
                          <>
                            <div className="criteria-row">
                              <label className="criteria-label">Criterion</label>
                              <input
                                className="criteria-title"
                                type="text"
                                placeholder="Short title (one line)"
                                value={c?.criterion ?? ''}
                                onChange={(e) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath
                                  ? { ...x, evaluation_criteria: (x.evaluation_criteria||[]).map((ci, idx) => idx===i?{ ...ci, criterion: e.target.value }:ci) }
                                  : x
                                ))}
                              />
                              <button className="small delete-criterion" type="button" title="Delete criterion" onClick={del}>Delete</button>
                            </div>
                            <div className="criteria-row">
                              <label className="criteria-label">Description</label>
                              <textarea
                                className="criteria-description"
                                placeholder="Longer description (supports multiple lines)"
                                value={c?.description ?? ''}
                                onChange={(e) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath
                                  ? { ...x, evaluation_criteria: (x.evaluation_criteria||[]).map((ci, idx) => idx===i?{ ...ci, description: e.target.value }:ci) }
                                  : x
                                ))}
                                rows={3}
                              />
                            </div>
                          </>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">No criteria available.</div>
                )}
              </div>
            </div>
          ) : entry.fileNumber === 1 ? (
            <div className="file1-structured">
              <textarea
                className="prose-textarea"
                value={entry.text || ''}
                onChange={(e) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath ? { ...x, text: e.target.value } : x))}
                rows={10}
              />
            </div>
          ) : (
            <textarea
            value={entry.text || ''}
            onChange={(e) => setExtractedEntries(prev => prev.map(x => x.filePath === entry.filePath ? { ...x, text: e.target.value } : x))}
            rows={Math.max(4, Math.min(14, (entry.text || '').split('\n').length))}
            />
          )}
          <div className="actions">
            <button
            className="run-btn"
            title="Save changes"
            onClick={() => handleUpdateFileContent(entry)}
            disabled={entry.loading}
            >
            Update
            </button>
          </div>
          </>
        )}
        </div>
        </React.Fragment>
      ))}
      </div>
    );
  };

  const extraSettingsModal = () => (
    <Modal
      isOpen={settingsModalOpen}
      onRequestClose={closeSettingsModal}
      contentLabel="Pipeline Runner Settings"
      className="pipeline-settings-modal"
      overlayClassName="pipeline-settings-overlay"
    >
      <div className="modal-header">
        <h2>Pipeline Runner Settings</h2>
        <button className="close-btn" onClick={closeSettingsModal} title="Close settings">&times;</button>
      </div>

      <div className="modal-body">
        <div className="field">
          <span>Pause after step</span>
          <div className="pause-choices" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13 }}>
              <input
                type="radio"
                name="pauseAfter"
                value={-1}
                checked={pauseAfterStep.length === 0}
                onChange={handleNeverChange}
                disabled={loading}
              />
              {' '}Never
            </label>
            {pipelinePrompts && pipelinePrompts.length > 0 ? (
              pipelinePrompts.map((p, idx) => (
                <label key={idx} style={{ fontSize: 13 }}>
                  <input
                    type="checkbox"
                    name="pauseAfter"
                    value={idx + 1}
                    checked={pauseAfterStep.includes(idx + 1)}
                    onChange={() => handlePauseAfterChange(idx + 1)}
                    disabled={loading}
                  />
                  {' '}After {idx + 1}
                </label>
              ))
            ) : (
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No steps available</span>
            )}
          </div>
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <span>Load checkpoint</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="number"
              min={0}
              placeholder="Step #"
              value={loadCheckpointStep}
              onChange={(e) => setLoadCheckpointStep(e.target.value)}
              style={{ width: 110, height: 36, borderRadius: 9999, padding: '0 10px' }}
              disabled={loading}
            />
            <button
              type="button"
              className="run-btn"
              onClick={() => { closeSettingsModal(); handleLoadCheckpoint(); }}
              disabled={loading || !chatId}
              title={chatId ? 'Load checkpoint for given chat and step' : 'No chat id available'}
              style={{ borderRadius: 9999, minHeight: 36, padding: '6px 14px' }}
            >
              Load checkpoint
            </button>
          </div>
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <span>Update specialist profile</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="run-btn"
              onClick={toggleUpdateProfileForm}
              disabled={loading || updatingProfile}
              title="Select a specialist and upload a profile file"
              style={{ borderRadius: 9999, minHeight: 36, padding: '6px 14px' }}
            >
              {showUpdateProfileForm ? 'Close updater' : 'Update profile'}
            </button>
            <button
              type="button"
              className="chat-toggle-btn"
              onClick={fetchSpecialistProfiles}
              disabled={loading || updatingProfile || specialistOptionsLoading}
              title="Refresh specialist list from HM-Specialists-dir"
              style={{ minHeight: 34 }}
            >
              {specialistOptionsLoading ? 'Refreshing…' : 'Refresh list'}
            </button>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Finds the existing .pptx profile in HM-Specialists-dir, deletes it, then uploads the new file with vision + processing.
            </span>
          </div>

          {showUpdateProfileForm ? (
            <div className="update-profile-card" style={{ marginTop: 10, padding: '10px 12px', border: '1px solid var(--border-muted, #d0d7de)', borderRadius: 12, background: 'var(--bg-tertiary)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label className="input-label" style={{ marginBottom: -2 }}>Specialist name</label>
                <select
                  value={updateProfileName}
                  onChange={(e) => { setUpdateProfileName(e.target.value); setUpdateProfileStatus(''); console.log('Uploading new profile file for', e.target.value); }}
                  disabled={loading || specialistOptionsLoading || updatingProfile || specialistNameOptions.length === 0}
                  style={{ height: 36 }}
                >
                  <option value="">Select a specialist…</option>
                  {specialistNameOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                {specialistOptionsError ? (
                  <div className="input-hint" style={{ color: 'var(--danger, #d1242f)' }}>{specialistOptionsError}</div>
                ) : null}
                {specialistOptionsLoading ? (
                  <div className="input-hint">Loading specialists from HM-Specialists-dir…</div>
                ) : null}
                {!specialistOptionsLoading && specialistOptionsError === '' && specialistNameOptions.length === 0 ? (
                  <div className="input-hint">No specialist profiles found in HM-Specialists-dir yet.</div>
                ) : null}

                <label className="input-label" style={{ marginBottom: -2, marginTop: 6 }}>Upload file</label>
                <input
                  type="file"
                  accept=".pptx"
                  onChange={handleUpdateProfileFileChange}
                  disabled={loading || updatingProfile}
                />

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="run-btn"
                    onClick={handleConfirmUpdateProfile}
                    disabled={loading || updatingProfile || specialistNameOptions.length === 0}
                    title="Delete the old profile and upload the new file"
                    style={{ borderRadius: 9999, minHeight: 36, padding: '6px 14px' }}
                  >
                    {updatingProfile ? 'Updating…' : 'Replace profile'}
                  </button>
                  {updateProfileFile ? (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{updateProfileFile.name}</span>
                  ) : null}
                </div>

                {updateProfileStatus ? (
                  <div className="input-hint" style={{ marginTop: 6 }}>{updateProfileStatus}</div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="modal-footer">
        <button className="close-btn" onClick={closeSettingsModal}>Close</button>
      </div>
    </Modal>
  );

return (
    <div className={`pipeline-runner-page${chatExpanded ? ' chat-expanded' : ''}`}>
      <div className="pipeline-runner-inner">
        <div className="left-pane">
            <h2>Pipeline Runner</h2>
            <div className="pipeline-config">
                <label className="field">
                    <span>Pipeline Name</span>
                    <input
                        type="text"
                        value={selectedPipeline}
                        onChange={(e) => setSelectedPipeline(e.target.value)}
                        placeholder="e.g. research_and_write_summary"
                        disabled={loading}
                    />
                </label>
                {/* Load previous chat (select unwired for now) */}
                <div className="field">
                  <span>Load previous chat</span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      className="chat-select"
                      value={selectedChatIdToLoad}
                      onChange={(e) => setSelectedChatIdToLoad(e.target.value)}
                      disabled={loading}
                      style={{ height: 36 }}
                      aria-label="Select a previous chat"
                    >
                      <option value="">Select a chat…</option>
                      {Array.isArray(availableChats) && availableChats.map((c) => {
                        const id = c?.id ?? c?.chat_id ?? c?._id;
                        const title = c?.chat_name || c?.user_query || `Chat ${id}`;
                        let dateStr = '';
                        try { if (c?.date) dateStr = new Date(c.date).toLocaleString(); } catch {}
                        return (
                          <option key={id} value={id}>
                            {title}{dateStr ? ` — ${dateStr}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      className="run-btn"
                      onClick={handleLoadPreviousChat}
                      disabled={loading || !selectedChatIdToLoad}
                      title="Load the selected chat history"
                    >
                      Load
                    </button>
                  </div>
                  {chatListError ? (
                    <div className="error-text" style={{ marginTop: 6 }}>{chatListError}</div>
                  ) : null}
                </div>
            </div>

            <div
              className="chat-box"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                flex: '1 1 auto'
              }}
            >
              <ChatShell isBusy={loading} onBackendBusyChange={onBackendBusyChange}>
                <div className="chat-header">
                  <h3>Pipeline Chat</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                    {chipEl}
                    <button
                      type="button"
                      className="chat-toggle-btn"
                      onClick={openSettingsModal}
                      title="Open pipeline settings"
                      disabled={loading}
                    >
                      Show Settings
                    </button>
                    <button
                      type="button"
                      className="chat-toggle-btn"
                      onClick={() => setChatExpanded(e => !e)}
                      title={chatExpanded ? 'Collapse chat' : 'Expand chat to full height'}
                    >
                      {chatExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>
                <div
                  className="chat-messages-scroll"
                  ref={outerScrollRef}
                  style={{
                    maxHeight: chatMaxHeight,
                    minHeight: chatMinHeight,
                    transition: 'max-height 0.2s ease, min-height 0.2s ease'
                  }}
                >
                  <ChatMessagesList
                    messages={annotatedDisplayedMessages}
                    loading={loading}
                    error={error}
                    hasMore={hasMoreMessages}
                    onLoadMore={loadMoreMessages}
                    markdownOptions={markdownOptions}
                    showAgentStepButtons={true}
                    onLoadAgentStep={handleLoadAgentStep}
                    chatId={chatId}
                    containerRef={messagesContainerRef}
                    scrollContainerRef={outerScrollRef}
                    displayedCount={displayedCount}
                    allCount={processedMessages.length}
                  />
                </div>
                {hasInitialSubmitted ? (
                  chatExpanded ? (
                    <form className="chat-input-bar" onSubmit={(e) => { e.preventDefault(); handleSecondarySend(); }}>
                      <input
                        className="chat-input"
                        value={secondaryInput}
                        onChange={(e) => setSecondaryInput(e.target.value)}
                        placeholder={pausedStepIndex >= 0 ? 'Add info for this step…' : 'Type info; it will apply at the next pause…'}
                        aria-label="Type your message"
                        disabled={loading}
                      />
                      <div className="chat-buttons">
                        <button
                          type="button"
                          className="chat-button cancel"
                          disabled={!loading}
                          onClick={handleCancelPipeline}
                          title="Cancel running pipeline"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="chat-button send"
                          disabled={loading || (!secondaryInput.trim() && pausedStepIndex < 0)}
                          title="Send Message"
                          aria-busy={loading}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/>
                          </svg>
                        </button>
                      </div>
                    </form>
                  ) : null
                ) : (
                <div className="input-column">
                  <div className="input-group">
                    <span className="input-label">Company Name</span>
                    <textarea
                      value={input.companyName}
                      onChange={(e) => setInput(prev => ({ ...prev, companyName: e.target.value }))}
                      onBlur={() => setTouched(prev => ({ ...prev, companyName: true }))}
                      placeholder="Please enter the company name..."
                      rows={1}
                      disabled={loading}
                      className={touched.companyName && companyNameError ? 'invalid' : ''}
                    />
                    {touched.companyName && companyNameError ? (
                      <div className="error-text">{companyNameError}</div>
                    ) : null}
                  </div>
                  <div className="input-group">
                    <span className="input-label">Company Domain URL</span>
                    <textarea
                      value={input.companyDomainUrl}
                      onChange={(e) => setInput(prev => ({ ...prev, companyDomainUrl: e.target.value }))}
                      onBlur={() => setTouched(prev => ({ ...prev, companyDomainUrl: true }))}
                      placeholder="https://www.cooperparry.com/ or cooperparry.com"
                      rows={1}
                      disabled={loading}
                      className={touched.companyDomainUrl && companyDomainUrlError ? 'invalid' : ''}
                    />
                    {touched.companyDomainUrl && companyDomainUrlError ? (
                      <div className="error-text">{companyDomainUrlError}</div>
                    ) : null}
                    <div className="input-hint">Tip: you can paste a full URL or just the domain.</div>
                  </div>
                  <div className="input-group">
                    <span className="input-label">Meeting Notes</span>
                    <textarea
                      value={input.meetingNotes}
                      onChange={(e) => setInput(prev => ({ ...prev, meetingNotes: e.target.value }))}
                      onBlur={() => setTouched(prev => ({ ...prev, meetingNotes: true }))}
                      placeholder="Meeting notes and any further information..."
                      rows={3}
                      disabled={loading}
                    />
                    <div className="input-hint">Optional: add any context you want the pipeline to consider.</div>
                  </div>
                  <div className="input-group">
                    <span className="input-label">Expected Start Date</span>
                    <textarea
                      value={input.expectedStartDate}
                      onChange={(e) => setInput(prev => ({ ...prev, expectedStartDate: e.target.value }))}
                      onBlur={() => setTouched(prev => ({ ...prev, expectedStartDate: true }))}
                      placeholder="DD/MM/YYYY"
                      rows={1}
                      disabled={loading}
                      className={touched.expectedStartDate && expectedStartDateError ? 'invalid' : ''}
                      style={{ width: '20%', minHeight: '24%' }}
                    />
                    {touched.expectedStartDate && expectedStartDateError ? (
                      <div className="error-text">{expectedStartDateError}</div>
                    ) : null}
                  
                    <div className="input-hint">Specify the expected project start date to help tailor specialist proposals.</div>
                  </div>
                  {/* Upload CSV and nicer action row */}
                  <div className="input-group upload-row" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <label className="input-label"><strong>Upload XLSX</strong></label>
                    <div className="file-upload">
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".xlsx"
                      onChange={handleCSVUpload}
                      disabled={loading}
                    />
                    </div>
                    <div className="input-hint" style={{ marginTop: '6px' }}>
                    Upload an XLSX of availability and it will be saved as
                    <code style={{ marginLeft: 6 }}>Specialists_Proposals/Specialist_Availability.xlsx</code>
                    . Supported: .xlsx
                    </div>
                  </div>

                  <div className="controls-sticky" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={startPipeline}
                      disabled={loading || !selectedPipeline || !input.companyName.trim() || !input.companyDomainUrl.trim() || !isValidCompanyDomainUrl(input.companyDomainUrl) || !input.expectedStartDate.trim()}
                      className="run-btn"
                      title={
                        !input.companyName.trim()
                          ? 'Enter company name first'
                          : !input.companyDomainUrl.trim()
                            ? 'Enter company domain URL first'
                            : !isValidCompanyDomainUrl(input.companyDomainUrl)
                              ? 'Enter a valid company domain URL'
                              : !input.expectedStartDate.trim()
                                ? 'Enter expected start date first'
                                : !selectedPipeline
                                  ? 'Select a pipeline'
                                  : 'Run pipeline'
                      }
                    >
                      {loading ? 'Running…' : 'Run Pipeline'}
                    </button>

                    <button
                      type="button"
                      className="secondary"
                      onClick={handleCancelPipeline}
                      disabled={!loading}
                      title="Cancel current run (requests pause and stops streaming)"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                      setInput({ companyName: '', companyDomainUrl: '', meetingNotes: '', expectedStartDate: '' });
                      setTouched({ companyName: false, companyDomainUrl: false, meetingNotes: false, expectedStartDate: false });
                      setMessages([]);
                      setError('');
                      setLogoLoadFailed(false);
                      setLogoCacheBuster(Date.now());
                      }}
                      disabled={loading && true}
                    >
                      Clear
                    </button>
                    {/* Load checkpoint and Resume controls moved below ChatShell to always be visible when chat is collapsed */}
                    </div>

                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    Tip: The pipeline will try to produce four files. You can edit extracted results on the right.
                    </div>
                    </div>
                    </div>
                  </div>
                  )}
                </ChatShell>
                {/* Controls visible when chat is collapsed */}
                {!chatExpanded && (
                  <div className="pipeline-controls" style={{ marginTop: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {pausedStepIndex >= 0 && (
                        <>
                          {/*<button
                            type="button"
                            className="run-btn"
                            onClick={() => sendMsgAction('')}
                            disabled={loading}
                            title="Msg (empty): continue the current step"
                            style={{ borderRadius: 9999, minHeight: 36, padding: '6px 14px' }}
                          >
                            Msg
                          </button>*/}
                          <button
                            type="button"
                            className="run-btn next-step"
                            onClick={sendNextStepAction}
                            disabled={loading}
                            title="Next Step: advance to the next step"
                            style={{ borderRadius: 9999, minHeight: 36, padding: '6px 14px' }}
                          >
                            Next Step
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="run-btn rerun-last-step"
                        onClick={rerunLastStep}
                        disabled={loading || !chatId || !(lastStepCompletedRef.current > 0)}
                        title={lastStepCompletedRef.current > 0 ? `Reload checkpoint before step ${lastStepCompletedRef.current} and rerun it` : 'No completed step to rerun yet'}
                        style={{ borderRadius: 9999, minHeight: 36, padding: '6px 14px' }}
                      >
                        Rerun last step
                      </button>
                    </div>
                  </div>
                )}
                  </div>
                </div>

        <div className="right-pane">
          <div className="results-header">
            <button className={`tab ${activeResultTab === 'extracted' ? 'active' : ''}`} onClick={() => setActiveResultTab('extracted')}>Extracted Results</button>
            <button className={`tab ${activeResultTab === 'files' ? 'active' : ''}`} onClick={() => setActiveResultTab('files')}>Raw Files</button>
            <div style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-muted)', alignSelf: 'center' }}>
              {Array.isArray(generatedFiles) && generatedFiles.length > 0 ? `${generatedFiles.length} file(s) reported` : 'No files yet'}
            </div>
          </div>
          <div className="results-body">
            {activeResultTab === 'extracted' ? renderExtracted() : renderFilesList()}
          </div>
        </div>

      </div>

        {extraSettingsModal()}

        <DocumentViewerModal
          isOpen={isViewerOpen}
          onClose={() => { setIsViewerOpen(false); setViewingFilePath(null); }}
            filePath={viewingFilePath}
            username={authUsername}
        />
        <div className="floating-actions">
          {finalizeError ? <div className="floating-error">{finalizeError}</div> : null}
          <button className="floating-btn" disabled={!canFinalize || finalizing} onClick={onFinalize} title={canFinalize ? 'Submit final proposal' : 'Waiting for all 4 files...'}>
            {finalizing ? 'Submitting…' : 'Submit Proposal'}
          </button>
        </div>
        <div className="floating-actions-2">
          {finalizeError ? <div className="floating-error">{finalizeError}</div> : null}
          <button className="floating-btn" disabled={!canFinalize || finalizing} onClick={saveChat} title={canFinalize ? 'Save chat' : 'Waiting for all 4 files...'}>
            {finalizing ? 'Saving...' : 'Save Pipeline'}
          </button>
        </div>
    </div>
);
}

export default PipelineRunnerPage;